// RichFormInstance — section-by-section form runner with autosave, photo upload,
// signature capture, submit and PDF download.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Save, CheckCircle, Loader2, Camera, X, FileText, Upload, ChevronRight, ChevronLeft,
    Sparkles, ClipboardList, AlertTriangle, MessageSquarePlus, Clock, Mail, CloudOff, Eye, EyeOff,
    Plus, ChevronUp, ChevronDown, Link2, Pencil,
} from "lucide-react";
import { api, formatApiError, API, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import Pill from "@/components/Pill";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { ImagePlus, Trash2, GripVertical } from "lucide-react";
import { Lock } from "lucide-react";
import { InstanceStatusActions } from "@/pages/forms/statusWorkflow";
import { EmailReportDialog } from "@/components/EmailReportDialog";
import { SyncStatusIndicator } from "@/components/SyncStatusIndicator";
import { saveDraft, getDraft, clearDraft } from "@/lib/offlineDraft";
import { evaluateShowIf, computeNewlyHiddenAnswered, effectiveGridRows, visibleSections as computeVisibleSections, FIRE_CHECK_CATEGORIES, stripNumberPrefix, isSectionHideable, sectionHasAnyVisibleField } from "@/lib/formLogic";
import { nowLondonIso, nowLondonHHMM, localInputToLondonIso, isoToLocalInput } from "@/lib/londonTime";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";
import { OtherDevicePendingBanner } from "@/components/OtherDevicePendingBanner";
import { clearOtherDevicePending } from "@/lib/photoFlusher";
import { computeKeyFindings } from "@/lib/formLogic";

const STATUS_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "red" };
const OWNER_LABEL = { client: "Client", pr: "Priority Risk", shared: "Shared" };
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);
const FULL_REPORT_CODES = new Set(["PR-CDM-SITE-INSPECTION", "BRPD-INSP", "BRPD-INSP-V3"]);
const BRANDING_CHOICE_CODES = new Set(["PR-CDM-SITE-INSPECTION", "BRPD-INSP", "BRPD-INSP-V3", "SHE-AUDIT", "QA-INSP", "SCAF-INSP"]);
const DOCX_EXPORT_CODES = new Set(["PR-CDM-SITE-INSPECTION", "BRPD-INSP", "BRPD-INSP-V3", "PL-ASPIRE-FC",
    "PL-ASPIRE-MFC-N1", "PL-ASPIRE-MFC-N2", "PL-ASPIRE-MFC-N3", "PL-ASPIRE-MFC-N4",
    "PL-ASPIRE-MFC-S1", "PL-ASPIRE-MFC-S2"]);

// Mirrors services/full_report.is_section_empty — a section with no answers,
// no photos and no comments won't appear in the final CDM/BSR report.
function isSectionEmpty(section, answers, photos, comments) {
    const secId = section.id;
    if ((photos || []).some((p) => p.section_id === secId)) return false;
    for (const fld of section.fields || []) {
        if (fld.field_type === "instruction" || fld.field_type === "signature") continue;
        if (fld.field_type === "photo") {
            if ((photos || []).some((p) => p.field_id === fld.id)) return false;
            continue;
        }
        const v = answers?.[fld.id];
        if (Array.isArray(v)) {
            if (v.length > 0) return false;
        } else if (v !== undefined && v !== null && v !== "") {
            return false;
        }
        if (comments?.[fld.id]) return false;
    }
    return true;
}

// Falls back to a friendly placeholder (instead of a broken-image icon) if a
// stored photo can't be loaded — e.g. a storage-side failure on an older file.
function PhotoImg({ src, alt, className, testId, draggable }) {
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <div data-testid={testId} className={`${className} flex flex-col items-center justify-center gap-1 bg-[#F6F4F0] text-[#9A968F] text-center px-1`}>
                <ImagePlus size={16} />
                <span className="text-[9px] leading-tight">Photo unavailable — please contact support</span>
            </div>
        );
    }
    return (
        <img data-testid={testId} src={src} alt={alt} className={className} draggable={draggable}
            onError={() => setFailed(true)} />
    );
}

// Row-level photo strip with editable captions — used by finding_repeater and
// subsection_list (Section 3 Findings / Section 2 Subsections) instead of the
// caption-less RowPhotoInline that CheckGridField uses internally.
function RowPhotoStrip({ rowId, photos = [], pendingPhotos = [], onPhoto, onDeletePhoto, onUpdatePhoto,
                        onRetryPhoto, onDiscardPhoto, uploading, readonly, instanceId }) {
    const rowPhotos = photos.filter((p) => p.row_id === rowId);
    const rowPending = pendingPhotos.filter((p) => p.row_id === rowId);
    return (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`row-photos-${rowId}`}>
            {rowPhotos.map((p) => (
                <div key={p.id} className="w-24">
                    <div className="relative w-24 h-24">
                        <PhotoImg testId={`row-photo-img-${p.id}`} src={`${API}/forms/instances/${instanceId}/photos/${p.id}/file`} alt={p.filename}
                            className="w-24 h-24 object-cover rounded-lg border border-[#E5E0DA]" />
                        {!readonly && onDeletePhoto && (
                            <button type="button" onClick={() => { if (window.confirm("Remove this photo?")) onDeletePhoto(p.id); }}
                                className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]"><X size={9} /></button>
                        )}
                    </div>
                    {readonly ? (
                        p.caption && <p className="text-[10px] text-[#6F6A64] mt-1 leading-tight">{p.caption}</p>
                    ) : (
                        <input data-testid={`row-photo-caption-${p.id}`} defaultValue={p.caption || ""} placeholder="Caption…"
                            onBlur={(e) => { if (e.target.value !== (p.caption || "")) onUpdatePhoto(p.id, { caption: e.target.value }); }}
                            className="w-24 mt-1 px-1.5 py-1 text-[10px] border border-[#E5E0DA] rounded outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                    )}
                </div>
            ))}
            <PendingPhotoThumbs pending={rowPending} testId={`row-pending-${rowId}`} onRetry={onRetryPhoto} onDiscard={readonly ? null : onDiscardPhoto} />
            {!readonly && onPhoto && (
                <label className="inline-flex flex-col items-center justify-center w-24 h-24 rounded-lg border border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)] cursor-pointer shrink-0 gap-1"
                    data-testid={`row-add-photo-${rowId}`}>
                    {uploading ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />}
                    <span className="text-[10px] font-semibold">Add photo</span>
                    <input type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => { onPhoto(e.target.files, rowId); e.target.value = ""; }} />
                </label>
            )}
        </div>
    );
}

export default function RichFormInstance() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const backTo = searchParams.get("back") || "/app/inspections";
    const backFromProject = backTo.startsWith("/app/projects/");
    const { user } = useAuth();
    const fileInputRef = useRef(null);

    const [instance, setInstance] = useState(null);
    const [template, setTemplate] = useState(null);
    const [answers, setAnswersState] = useState({});
    const answersRef = useRef({});
    const setAnswers = useCallback((updater) => {
        setAnswersState((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            answersRef.current = next;
            return next;
        });
    }, []);
    const [signature, setSignatureState] = useState("");
    const signatureRef = useRef("");
    const setSignature = useCallback((v) => {
        signatureRef.current = v;
        setSignatureState(v);
    }, []);
    const [comments, setCommentsState] = useState({});
    const commentsRef = useRef({});
    const setComments = useCallback((updater) => {
        setCommentsState((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            commentsRef.current = next;
            return next;
        });
    }, []);
    const [instanceActions, setInstanceActions] = useState([]);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [staff, setStaff] = useState([]);
    const [timeStart, setTimeStart] = useState("");
    const [timeEnd, setTimeEnd] = useState("");
    const [raiseFor, setRaiseFor] = useState(null);      // {fieldId, label} | null
    const [aiBusy, setAiBusy] = useState(false);
    const [activeSection, setActiveSection] = useState(0);
    const [pendingAnswerChange, setPendingAnswerChange] = useState(null); // {fid, v, hiddenItems} | null — show_if confirm
    const [unlockedDropdowns, setUnlockedDropdowns] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [syncStatus, setSyncStatus] = useState("saved"); // saving | saved | offline | error
    const [submitting, setSubmitting] = useState(false);
    const [uploadingFor, setUploadingFor] = useState(null);
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const lastAutoAppliedRef = useRef({});
    useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
    const [emailOpen, setEmailOpen] = useState(false);
    const [hideBusy, setHideBusy] = useState(false);
    const draftKey = `instance:${id}`;

    const reload = useCallback(async () => {
        const { data: inst } = await api.get(`/forms/instances/${id}`);
        setInstance(inst);
        setAnswers(inst.answers || {});
        answersRef.current = inst.answers || {};
        setComments(inst.field_comments || {});
        commentsRef.current = inst.field_comments || {};
        setSignature(inst.signature_data || "");
        signatureRef.current = inst.signature_data || "";
        setTimeStart(inst.time_started || "");
        setTimeEnd(inst.time_ended || "");
        // A finalized report (Submitted/Approved) always renders from its frozen
        // template_snapshot — what was actually reviewed/approved never silently
        // reflows later. A still-editable Draft/Rejected report always renders
        // from the CURRENT live template instead, even if an old snapshot is
        // sitting on the record from an earlier submit — otherwise a reopened
        // report would keep showing a stale structure forever (e.g. missing a
        // since-added field/column on a long-running template like the weekly
        // Aspire Fire Check).
        if (inst.template_snapshot && ["Submitted", "Approved"].includes(inst.status)) {
            setTemplate(inst.template_snapshot);
        } else {
            const { data: tpl } = await api.get(`/forms/templates/${inst.template_id}`);
            setTemplate(tpl);
        }
        try {
            const { data: acts } = await api.get(`/forms/instances/${id}/actions`);
            setInstanceActions(acts || []);
        } catch { setInstanceActions([]); }
        if (["Submitted", "Approved"].includes(inst.status)) {
            clearDraft(draftKey).catch(() => {});
        }
        return inst;
    }, [id, setAnswers, setComments, setSignature, draftKey]);

    // On first load, prefer any unsynced local draft over the server copy —
    // protects work that was entered while offline/before a crash or reload.
    useEffect(() => {
        (async () => {
            try {
                await reload();
                const local = await getDraft(draftKey);
                if (local && local.dirty) {
                    setAnswers(local.data.answers || {});
                    answersRef.current = local.data.answers || {};
                    setComments(local.data.field_comments || {});
                    commentsRef.current = local.data.field_comments || {};
                    if (local.data.signature_data) {
                        setSignature(local.data.signature_data);
                        signatureRef.current = local.data.signature_data;
                    }
                    setDirty(true);
                    toast.info("Restored unsaved changes from before — syncing now.");
                }
            } catch (e) {
                toast.error("Could not load inspection");
                navigate(backTo);
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Shared offline photo queue (lib/photoFlusher.js) — compress -> IndexedDB
    // write-ahead -> upload with backoff. Same pipeline every other screen in the
    // app plugs into via photoUploadTargets.js.
    const {
        pendingPhotos,
        queuePhoto: queuePhotoOnly,
        retry: retryQueuedPhoto,
        discard: discardQueuedPhoto,
        discardAll: discardAllPendingPhotos,
        flush: flushQueuedPhotos,
    } = useOfflinePhotoQueue({ entityType: "form_instance", entityId: id, draftKey, enabled: !loading, onUploaded: reload });
    const batchTotalRef = useRef(0);
    const [showSyncedBadge, setShowSyncedBadge] = useState(false);
    useEffect(() => {
        if (pendingPhotos.length > batchTotalRef.current) batchTotalRef.current = pendingPhotos.length;
        if (pendingPhotos.length === 0) {
            if (batchTotalRef.current > 0) {
                setShowSyncedBadge(true);
                const t = setTimeout(() => setShowSyncedBadge(false), 4000);
                batchTotalRef.current = 0;
                return () => clearTimeout(t);
            }
        }
    }, [pendingPhotos.length]);

    const isPortalRole = !!user && ["client_admin", "site_manager"].includes(user.role);
    const portalLocked = isPortalRole && (!template || template.completed_by !== "client");
    const readonly = !user || !CAN_EDIT_ROLES.has(user.role) || portalLocked || (instance && instance.status !== "Draft" && instance.status !== "Rejected");

    const isInternal = !!user && ["director", "administrator", "consultant"].includes(user.role);

    // Keep the active section pointer valid when show_if evaluation hides the
    // currently-open section (e.g. a controlling answer just changed).
    useEffect(() => {
        if (!template) return;
        const visible = (template.sections || []).filter((s) => s.enabled !== false && evaluateShowIf(s.show_if, answers));
        if (visible.length && activeSection >= visible.length) setActiveSection(visible.length - 1);
    }, [template, answers, activeSection]);

    useEffect(() => {
        if (!isInternal) return;
        api.get("/forms/staff").then(({ data }) => setStaff(data || [])).catch(() => setStaff([]));
    }, [isInternal]);

    const saveTime = async (field, val) => {
        const prev = field === "time_started" ? timeStart : timeEnd;
        if (field === "time_started") setTimeStart(val); else setTimeEnd(val);
        try {
            await api.patch(`/forms/instances/${id}`, { [field]: val || undefined });
            setInstance((p) => (p ? { ...p, [field]: val } : p));
        } catch (e) {
            if (field === "time_started") setTimeStart(prev); else setTimeEnd(prev);
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save time");
        }
    };

    // Fast local persistence (~350ms) — survives a reload/crash/tab-close even
    // before the slower remote autosave below has had a chance to run.
    useEffect(() => {
        if (!dirty || readonly) return;
        const handle = setTimeout(() => {
            saveDraft(draftKey, {
                answers: answersRef.current,
                field_comments: commentsRef.current,
                signature_data: signatureRef.current,
            }, true);
        }, 350);
        return () => clearTimeout(handle);
    }, [answers, signature, comments, dirty, readonly, draftKey]);

    // Debounced remote autosave (1.2s after last change). If the network is
    // down the data stays safe in the local draft above and syncStatus flips
    // to "offline" instead of surfacing a scary error toast.
    useEffect(() => {
        if (!dirty || readonly) return;
        const handle = setTimeout(async () => {
            if (!navigator.onLine) { setSyncStatus("offline"); return; }
            setSyncStatus("saving");
            try {
                await api.patch(`/forms/instances/${id}`, {
                    answers: answersRef.current,
                    field_comments: commentsRef.current,
                    signature_data: signatureRef.current || undefined,
                });
                setDirty(false);
                setSyncStatus("saved");
                saveDraft(draftKey, {
                    answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current,
                }, false);
            } catch (e) {
                if (!e.response) { setSyncStatus("offline"); }
                else { setSyncStatus("error"); toast.error(formatApiError(e.response?.data?.detail) || "Autosave failed"); }
            }
        }, 1200);
        return () => clearTimeout(handle);
    }, [answers, signature, dirty, id, readonly, draftKey]);

    const applyAnswer = (fid, v) => {
        setAnswers((a) => ({ ...a, [fid]: v }));
        setDirty(true);
    };

    const setAnswer = (fid, v) => {
        if (template && !readonly) {
            const hidden = computeNewlyHiddenAnswered(template, answersRef.current, fid, v);
            if (hidden.length) {
                setPendingAnswerChange({ fid, v, hiddenItems: hidden });
                return;
            }
        }
        applyAnswer(fid, v);
    };

    const setComment = (fid, v) => {
        setComments((c) => ({ ...c, [fid]: v }));
        setDirty(true);
    };

    // Stairwell-style fields: auto-derive + lock a dropdown's value from
    // another field's answer (dropdown_config.linked_field_map), same
    // lock/Unlock UX as the existing building_map rotation. Re-applies while
    // the current answer is still blank, OR still matches the auto value we
    // (or the saved instance, on reload) last saw it following — so it keeps
    // tracking live Building changes, but a value that was deliberately
    // changed away from auto is never clobbered, even across a reload.
    useEffect(() => {
        if (!template || readonly) return;
        for (const s of template.sections || []) {
            for (const f of s.fields || []) {
                const lfm = f.dropdown_config?.linked_field_map;
                if (!lfm) continue;
                const auto = (lfm.value_map || {})[answers[lfm.field_id]];
                if (!auto) continue;
                const current = answers[f.id];
                if (current === auto) {
                    lastAutoAppliedRef.current[f.id] = auto; // still following — keep tracking
                    continue;
                }
                if (!current || lastAutoAppliedRef.current[f.id] === current) {
                    lastAutoAppliedRef.current[f.id] = auto;
                    applyAnswer(f.id, auto);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, answers, readonly]);

    // Fire checks only — auto-fill Finished the moment "Inspection complete?"
    // is set to Yes, if it isn't already set.
    useEffect(() => {
        if (!template || readonly || !FIRE_CHECK_CATEGORIES.has(template.category) || timeEnd) return;
        const compField = (template.sections || []).flatMap((s) => s.fields || []).find((f) => f.label === "Inspection complete?");
        if (compField && (answers[compField.id] || "").toLowerCase() === "yes") {
            saveTime("time_ended", nowLondonIso());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, template, readonly, timeEnd]);

    const handleSignatureChange = (val) => {
        if (val === signatureRef.current) return;
        signatureRef.current = val;
        setSignature(val);
        setDirty(true);
    };

    // On reconnect: push any dirty answers, then flush the photo queue.
    useEffect(() => {
        const goOnline = async () => {
            setSyncStatus("saving");
            try {
                if (dirtyRef.current) {
                    await api.patch(`/forms/instances/${id}`, {
                        answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current || undefined,
                    });
                    setDirty(false);
                    saveDraft(draftKey, {
                        answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current,
                    }, false);
                }
                await flushQueuedPhotos();
                setSyncStatus("saved");
                toast.success("Back online — your changes are synced");
            } catch {
                setSyncStatus("offline");
            }
        };
        const goOffline = () => setSyncStatus("offline");
        window.addEventListener("online", goOnline);
        window.addEventListener("offline", goOffline);
        return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
    }, [id, draftKey, flushQueuedPhotos]);

    const handleSaveNow = async () => {
        if (!navigator.onLine) {
            setSyncStatus("offline");
            toast.info("You're offline — your changes are saved on this device and will sync automatically.");
            return;
        }
        setSyncStatus("saving");
        try {
            await api.patch(`/forms/instances/${id}`, {
                answers: answersRef.current,
                field_comments: commentsRef.current,
                signature_data: signatureRef.current || undefined,
            });
            toast.success("Saved");
            setDirty(false);
            setSyncStatus("saved");
            saveDraft(draftKey, {
                answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current,
            }, false);
        } catch (e) {
            if (!e.response) { setSyncStatus("offline"); toast.info("You're offline — your changes are saved on this device and will sync automatically."); }
            else { setSyncStatus("error"); toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        }
    };

    const submitAction = async (fieldId, payload, rowId, title) => {
        try {
            const body = { ...payload, ...(rowId ? { row_id: rowId } : {}), ...(title ? { title } : {}) };
            await api.post(`/forms/instances/${id}/questions/${fieldId}/action`, body);
            setRaiseFor(null);
            await flushAutosave();
            await reload();
            toast.success("Action raised");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not raise action");
        }
    };

    const linkAction = async (fieldId, actionId, rowId) => {
        try {
            await api.post(`/forms/instances/${id}/questions/${fieldId}/link-action`, { action_id: actionId, row_id: rowId });
            await flushAutosave();
            await reload();
            toast.success("Action linked");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not link action");
        }
    };

    const updateFieldLabel = async (fieldId, label) => {
        const next = { ...(instance.field_label_overrides || {}), [fieldId]: label };
        try {
            await api.patch(`/forms/instances/${id}`, { field_label_overrides: next });
            setInstance((p) => (p ? { ...p, field_label_overrides: next } : p));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not rename heading");
        }
    };

    const updateReportBranding = async (val) => {
        try {
            await api.patch(`/forms/instances/${id}`, { report_branding: val });
            setInstance((p) => (p ? { ...p, report_branding: val } : p));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update branding");
        }
    };

    const generateAiSummary = async (fieldId) => {
        setAiBusy(true);
        try {
            const { data } = await api.post(`/forms/instances/${id}/ai-summary`);
            setAnswer(fieldId, data.draft);
            toast.success(`AI draft ready (${data.failed_count} non-compliant, ${data.actions_count} actions)`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI draft failed");
        }
        setAiBusy(false);
    };

    const flushAutosave = async () => {
        if (!dirtyRef.current || readonly) return;
        try {
            await api.patch(`/forms/instances/${id}`, {
                answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current || undefined,
            });
            setDirty(false);
            setSyncStatus("saved");
            saveDraft(draftKey, {
                answers: answersRef.current, field_comments: commentsRef.current, signature_data: signatureRef.current,
            }, false);
        } catch { /* best-effort — the 350ms local draft still protects the data either way */ }
    };

    const handlePhotoUpload = async (fieldId, fileList, rowId) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;
        await flushAutosave();
        setUploadingFor(fieldId);
        const coverSection = (template?.sections || []).find((s) => s.title?.trim().toLowerCase() === "front cover");
        const coverField = (coverSection?.fields || []).find((f) => f.field_type === "photo");
        for (const file of files) {
            try {
                await queuePhotoOnly(file, {
                    field_id: fieldId,
                    ...(rowId ? { row_id: rowId } : {}),
                    preserve_original: fieldId === coverField?.id,
                });
            }
            catch { toast.error(`Could not save ${file.name} on this device`); }
        }
        setUploadingFor(null);
        if (navigator.onLine) flushQueuedPhotos();
        else toast.info(`${files.length} photo${files.length !== 1 ? "s" : ""} saved on this device — will upload automatically once you're back online.`);
    };

    const handleSectionPhotoUpload = async (sectionId, fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;
        setUploadingFor(`sec-${sectionId}`);
        for (const file of files) {
            try { await queuePhotoOnly(file, { section_id: sectionId, anchor: "bottom" }); }
            catch { toast.error(`Could not save ${file.name} on this device`); }
        }
        setUploadingFor(null);
        if (navigator.onLine) flushQueuedPhotos();
        else toast.info(`${files.length} image${files.length !== 1 ? "s" : ""} saved on this device — will upload automatically once you're back online.`);
    };

    const updatePhoto = async (photoId, patch) => {
        try {
            await api.patch(`/forms/instances/${id}/photos/${photoId}`, patch);
            await reload();
        } catch { toast.error("Could not update image"); }
    };

    const reorderPhotos = async (changes) => {
        if (!changes?.length) return;
        try {
            // Sequential, not Promise.all — the backend does a whole-document
            // read-modify-write on the embedded photos array per PATCH, so
            // concurrent requests would race and silently drop updates.
            for (const { id: photoId, sort_order } of changes) {
                await api.patch(`/forms/instances/${id}/photos/${photoId}`, { sort_order });
            }
            await reload();
        } catch { toast.error("Could not reorder images"); }
    };

    const deletePhoto = async (photoId) => {
        try {
            await api.delete(`/forms/instances/${id}/photos/${photoId}`);
            await reload();
            toast.success("Image removed");
        } catch { toast.error("Could not remove image"); }
    };

    // Save latest + client-side validate before a Draft→Submitted transition.
    // Returns false to abort the submit (the workflow component then does the actual /submit call).
    // Pre-flight guards only — the submission/approval state machine itself is untouched.
    const prepareSubmit = async () => {
        if (!navigator.onLine) {
            toast.error("You're offline — please reconnect to submit. Your answers are safely saved on this device.");
            return false;
        }
        if (dirty || pendingPhotos.length > 0) {
            const n = pendingPhotos.length;
            toast.error(n > 0
                ? `Still uploading ${n} photo${n !== 1 ? "s" : ""} — please wait a moment and try again.`
                : "Still syncing your latest changes — please wait a moment and try again.");
            return false;
        }
        const missing = [];
        const hiddenSecIds = new Set((instance.hidden_sections || []).map((h) => h.section_id));
        (template?.sections || []).forEach((sec) => {
            if (hiddenSecIds.has(sec.id)) return;
            const secTitle = stripNumberPrefix(sec.title);
            const secVisible = sec.enabled !== false && evaluateShowIf(sec.show_if, answers);
            (sec.fields || []).forEach((f) => {
                const fieldVisible = evaluateShowIf(f.show_if, answers);
                const condRequired = !!(f.required_if && secVisible && fieldVisible && evaluateShowIf(f.required_if, answers));
                if (!f.required && !condRequired) return;
                if (["instruction"].includes(f.field_type)) return;
                if (f.field_type === "photo") {
                    const has = (instance.photos || []).some((p) => p.field_id === f.id);
                    if (!has) missing.push(`${secTitle}: ${f.label}`);
                    return;
                }
                if (f.field_type === "signature") return;
                const v = answers[f.id];
                if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) {
                    missing.push(`${secTitle}: ${f.label}`);
                }
            });
            const ptField = (sec.fields || []).find((x) => x.wsr_role === "person_type");
            const ptoField = (sec.fields || []).find((x) => x.wsr_role === "person_type_other");
            if (ptField && ptoField && answers[ptField.id] === "Other" && !(answers[ptoField.id] || "").trim()) {
                missing.push(`${secTitle}: ${ptoField.label}`);
            }
        });
        if (missing.length) {
            toast.error(`Missing required: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`);
            return false;
        }
        if (template?.requires_signature && !signatureRef.current) {
            toast.error("Signature is required before submitting");
            return false;
        }
        try {
            await api.patch(`/forms/instances/${id}`, {
                answers: answersRef.current,
                field_comments: commentsRef.current,
                signature_data: signatureRef.current || undefined,
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save before submitting");
            return false;
        }
        return true;
    };

    const hideSectionNow = async (sectionId) => {
        setHideBusy(true);
        try {
            await api.post(`/forms/instances/${id}/sections/${sectionId}/hide`, {});
            await reload();
            toast.success("Section hidden from this report");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not hide this section");
        } finally {
            setHideBusy(false);
        }
    };

    const showSection = async (sectionId) => {
        try {
            await api.post(`/forms/instances/${id}/sections/${sectionId}/show`);
            await reload();
            toast.success("Section restored to this report");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not show this section");
        }
    };

    const downloadPdf = async () => {
        if (pdfBusy) return;
        setPdfBusy(true);
        const t = toast.loading("Generating PDF… this can take a moment for reports with photos.");
        try {
            const res = await api.get(`/forms/instances/${id}/pdf`, { responseType: "blob", timeout: 180000 });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `${instance.template_code}-${id.slice(0, 8)}.pdf`);
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF ready", { id: t });
        } catch (e) {
            toast.error("PDF generation failed. Please try again in a moment.", { id: t });
        } finally {
            setPdfBusy(false);
        }
    };

    const [docxBusy, setDocxBusy] = useState(false);
    const downloadDocx = async () => {
        if (docxBusy) return;
        setDocxBusy(true);
        const t = toast.loading("Generating Word document…");
        try {
            const res = await api.get(`/forms/instances/${id}/docx`, { responseType: "blob", timeout: 180000 });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `${instance.template_code}-${id.slice(0, 8)}.docx`);
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Word document ready", { id: t });
        } catch (e) {
            toast.error("Word export failed. Please try again in a moment.", { id: t });
        } finally {
            setDocxBusy(false);
        }
    };

    if (loading) {
        return <div className="p-10 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading inspection…</div>;
    }
    if (!instance || !template) return <div className="p-10 text-center text-[#9A968F]">Not found.</div>;

    const isFireCheckCat = FIRE_CHECK_CATEGORIES.has(template.category);
    const hiddenSectionIdSet = new Set((instance.hidden_sections || []).map((h) => h.section_id));
    // Fire-check sections whose fields are ALL hidden by the current
    // building/week gates are phantom — dropping them from the (unrenumbered)
    // export list too, same as the renumbered PDF/Word export does below, so
    // the nav badge/title never fall back to a stale raw "06 - ..." number
    // that collides with the correctly-renumbered section next to it.
    // Sections the user explicitly hid stay in the nav (greyed, with a
    // Show toggle) regardless of emptiness.
    const navSections = (template.sections || []).filter((s) => s.enabled !== false && evaluateShowIf(s.show_if, answers)
        && (!isFireCheckCat || hiddenSectionIdSet.has(s.id) || sectionHasAnyVisibleField(s, answers)));
    const numberedSections = isFireCheckCat ? computeVisibleSections(template, instance, answers) : [];
    const titleById = new Map(numberedSections.map((s) => [s.id, s.display_title]));
    const numberById = new Map(numberedSections.map((s, i) => [s.id, i + 1]));
    const hiddenMap = new Map((instance.hidden_sections || []).map((h) => [h.section_id, h]));
    const canToggleSections = isFireCheckCat && isInternal && !readonly;

    // Mirrors services/full_report.orphaned_photos — photos left behind by a
    // legacy template id rebuild (see instance.legacy_data_recovered).
    const currentSectionIds = new Set((template.sections || []).map((s) => s.id));
    const currentFieldIds = new Set((template.sections || []).flatMap((s) => (s.fields || []).map((f) => f.id)));
    const legacyOrphanedPhotos = (instance.photos || []).filter((p) =>
        !(p.section_id && currentSectionIds.has(p.section_id)) && !(p.field_id && currentFieldIds.has(p.field_id)));

    const sec = navSections[activeSection];
    const secIsHidden = sec && hiddenMap.has(sec.id);
    const secLabel = sec ? (isFireCheckCat ? (secIsHidden ? stripNumberPrefix(sec.title) : (titleById.get(sec.id) || sec.title)) : sec.title) : "";

    return (
        <div data-testid="rich-form-instance" className="space-y-5">
            {pendingAnswerChange && (
                <div className="fixed inset-0 z-[70] grid place-items-center p-4" style={{ background: "rgba(10,10,10,0.5)" }}
                    data-testid="show-if-hide-confirm-dialog">
                    <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-2xl border border-[#E5E0DA]">
                        <p className="text-[14px] font-bold text-[#1F1F1F] flex items-center gap-2">
                            <AlertTriangle size={15} style={{ color: "#C68A1C" }} /> Hide already-answered items?
                        </p>
                        <p className="text-[12.5px] text-[#6F6A64] mt-1.5">
                            Changing this will hide <b>{pendingAnswerChange.hiddenItems.join(", ")}</b>. Your answers are kept but won't be included in this report.
                        </p>
                        <div className="flex justify-end gap-2 mt-3">
                            <button data-testid="show-if-hide-cancel" onClick={() => setPendingAnswerChange(null)}
                                className="px-3 py-1.5 text-[12.5px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                            <button data-testid="show-if-hide-confirm"
                                onClick={() => { applyAnswer(pendingAnswerChange.fid, pendingAnswerChange.v); setPendingAnswerChange(null); }}
                                className="px-3 py-1.5 text-[12.5px] font-semibold rounded-lg text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>Continue</button>
                        </div>
                    </div>
                </div>
            )}
            {instance.evidence_status === "outstanding" && (
                <div data-testid="evidence-outstanding-banner" className="flex items-center gap-2.5 rounded-xl px-4 py-3 border" style={{ background: "#FBE9E7", borderColor: "#F3C4BC" }}>
                    <AlertTriangle size={16} className="text-[#B33A2E] shrink-0" />
                    <p className="text-[12.5px] text-[#8A2C22]"><b>Essential documents not received.</b> This operative has not evidenced their required competencies (e.g. CSCS card). Chase the evidence or restrict site entry before authorising.</p>
                </div>
            )}
            {(pendingPhotos.length > 0 || showSyncedBadge) && (
                <div data-testid="photo-sync-status" className="flex items-center gap-2.5 rounded-xl px-4 py-3 border" style={{ background: "#FBEFE3", borderColor: "#F0DCC2" }}>
                    <Camera size={16} className="text-[#9F4212] shrink-0" />
                    <p className="text-[12.5px] text-[#9F4212] font-semibold flex-1">
                        {pendingPhotos.length === 0 ? "All photos uploaded ✓"
                            : pendingPhotos.every((p) => p.failed)
                                ? `${pendingPhotos.length} photo${pendingPhotos.length !== 1 ? "s" : ""} failed to upload — tap to retry or discard below.`
                            : syncStatus === "offline"
                                ? `${pendingPhotos.length} photo${pendingPhotos.length !== 1 ? "s" : ""} waiting to upload — keep this report open when you have signal.`
                            : pendingPhotos.every((p) => !p.failed && p.nextRetryAt && p.nextRetryAt > Date.now())
                                ? `${pendingPhotos.length} photo${pendingPhotos.length !== 1 ? "s" : ""} hit a network error — retrying automatically, keep this report open.`
                                : `Uploading ${Math.max(0, batchTotalRef.current - pendingPhotos.length)} of ${batchTotalRef.current}…`}
                    </p>
                    {pendingPhotos.length > 0 && !readonly && (
                        <button type="button" data-testid="discard-all-pending-photos"
                            onClick={() => { if (window.confirm(`Discard all ${pendingPhotos.length} pending photo(s)? They will not be uploaded.`)) discardAllPendingPhotos(); }}
                            className="text-[11px] font-semibold text-[#9F4212] hover:underline shrink-0">
                            Discard all
                        </button>
                    )}
                </div>
            )}
            <OtherDevicePendingBanner pendingUploads={instance.pending_uploads} canClear={!readonly}
                onClear={async (devId) => { await clearOtherDevicePending("form_instance", id, devId); await reload(); }} />
            {/* Header */}
            <div className="flex items-center gap-3 flex-wrap">
                <button data-testid="instance-back" onClick={() => navigate(backTo)}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-[#F6F4F0] text-[#6F6A64] text-[12px] font-semibold">
                    <ArrowLeft size={16} />
                    {backFromProject ? "Back to project" : ""}
                </button>
                <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-[#1F1F1F] truncate">{instance.title || template.name}</h2>
                    <p className="text-[12px] text-[#6F6A64]">
                        {template.name} · {template.code} · v{instance.template_version}
                        {instance.assignee_name && <> · Assigned by {instance.assignee_name}</>}
                        {instance.due_date && <> · Due {instance.due_date}</>}
                    </p>
                </div>
                <Pill tone={STATUS_TONE[instance.status] || "neutral"}>{instance.status}</Pill>
                <span data-testid="instance-owner-pill"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-[#E5E0DA] text-[#6F6A64] bg-white"
                    title="Who owns this completed report — for reporting and accountability. Not the same as who's allowed to run this form (set on the template).">
                    Owner: {OWNER_LABEL[instance.responsible_party] || "Priority Risk"}
                </span>
                {instance.approval_gate === false && (
                    <span data-testid="instance-no-approval-pill"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold"
                        style={{ background: "#E8F1E6", color: "#3F7D3A" }}
                        title="This report is client-owned and has no Priority Risk signatory — Submitted is the completed state.">
                        No PR approval needed
                    </span>
                )}
                {instance.approval_gate !== false && template.pr_approval_required && (
                    <span data-testid="instance-pr-approval-pill"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold"
                        style={{ background: "#FBEFE3", color: "#9F4212" }}
                        title="Priority Risk must approve this report after it is submitted.">
                        PR approval required
                    </span>
                )}
                {(instance.photos || []).length > 0 && (
                    <span data-testid="instance-photo-count"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-[#E5E0DA] text-[#6F6A64] bg-white"
                        title={`${(instance.photos || []).length} photo(s) attached — included in the PDF`}>
                        <Camera size={12} /> {(instance.photos || []).length}
                    </span>
                )}
                {!readonly && <SyncStatusIndicator status={syncStatus} testId="instance-sync-status" />}
                {!readonly && (
                    <button data-testid="instance-save" onClick={handleSaveNow} disabled={syncStatus === "saving"}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60">
                        <Save size={13} /> Save
                    </button>
                )}
                {(instance.status === "Submitted" || instance.status === "Approved" || FULL_REPORT_CODES.has(template.code)) && (
                    <button data-testid="instance-pdf" onClick={downloadPdf} disabled={pdfBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60 disabled:cursor-wait">
                        {pdfBusy ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : <><FileText size={13} /> PDF</>}
                    </button>
                )}
                {(instance.status === "Submitted" || instance.status === "Approved" || FULL_REPORT_CODES.has(template.code)) && DOCX_EXPORT_CODES.has(template.code) && (
                    <button data-testid="instance-docx" onClick={downloadDocx} disabled={docxBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60 disabled:cursor-wait">
                        {docxBusy ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : <><FileText size={13} /> Word</>}
                    </button>
                )}
                {(instance.status === "Submitted" || instance.status === "Approved") && (
                    <button data-testid="instance-email" onClick={() => setEmailOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0]">
                        <Mail size={13} /> Email to client
                    </button>
                )}
                <AdminDeleteButton endpoint={`/forms/instances/${id}`} label="audit / inspection"
                    variant="button" testId="instance-delete"
                    onDeleted={() => navigate(backTo)} />
                {!readonly && instance.status === "Draft" && (
                    <InstanceStatusActions instance={instance} role={user?.role} variant="buttons"
                        beforeSubmit={prepareSubmit} onChanged={reload} />
                )}
                {instance.status !== "Draft" && !portalLocked && (
                    <InstanceStatusActions instance={instance} role={user?.role} variant="buttons" onChanged={reload} />
                )}
            </div>

            {instance.status === "Rejected" && instance.decision_note && (
                <div data-testid="instance-rejection-note" className="rounded-lg px-3 py-2 text-[12.5px]" style={{ background: "#FBEDEC", color: "#8E2E25" }}>
                    <b>Rejected:</b> {instance.decision_note}
                </div>
            )}

            {instance.legacy_data_recovered && (
                <div data-testid="instance-legacy-recovered-banner" className="rounded-lg px-3 py-3 text-[12.5px] space-y-2" style={{ background: "#FEF3E2", color: "#7A5300" }}>
                    {(instance.legacy_recovered_notes || []).length > 0 || legacyOrphanedPhotos.length > 0 ? (
                        <p><b>Recovered legacy findings:</b> this report was created under an earlier version of this template.
                            {" "}The entries below were captured at the time but couldn't be automatically re-matched to a specific
                            section after a template update — nothing has been lost, please review and cross-check against the
                            sections above. {
                                        ["BRPD-INSP", "BRPD-INSP-V3"].includes(template.code || instance.template_code)
                                            ? " These recovery notes and photos are shown here for review and are not included in the PDF/Word exports."
                                            : " They are also included in the PDF/Word export as an appendix."
                                    }</p>
                    ) : (
                        <p><b>Legacy data restored:</b> this report was created under an earlier version of this template —
                            its answers and photos have been automatically re-matched to the current sections.</p>
                    )}
                    {(instance.legacy_recovered_notes || []).length > 0 && (
                        <ul className="list-disc pl-5 space-y-1">
                            {instance.legacy_recovered_notes.map((note, i) => (
                                <li key={i} data-testid={`legacy-recovered-note-${i}`}>{note}</li>
                            ))}
                        </ul>
                    )}
                    {legacyOrphanedPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1" data-testid="legacy-recovered-photos">
                            {legacyOrphanedPhotos.map((p) => (
                                <img key={p.id} src={`${API}/forms/instances/${id}/photos/${p.id}/file`} alt={p.caption || p.filename}
                                    title={p.caption || p.filename} className="w-20 h-20 object-cover rounded-lg border border-[#E9D9B6]" />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {isInternal && !isFireCheckCat && (
                <TimeEffortCard
                    timeStart={timeStart} timeEnd={timeEnd}
                    onChange={saveTime} readonly={readonly} fireCheck={false} />
            )}

            <div className="grid grid-cols-[220px_1fr] gap-5">
                {/* Section nav */}
                <div className="bg-white border border-[#E5E0DA] rounded-2xl p-2 h-fit">
                    <p className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Sections</p>
                    {navSections.map((s, idx) => {
                        const isHidden = hiddenMap.has(s.id);
                        const showEmptyHint = !isHidden && FULL_REPORT_CODES.has(template.code) && s.title.trim().toLowerCase() !== "front cover"
                            && isSectionEmpty(s, answers, instance.photos, comments);
                        const label = isFireCheckCat ? (isHidden ? stripNumberPrefix(s.title) : (titleById.get(s.id) || s.title)) : s.title;
                        return (
                        <button key={s.id}
                            data-testid={`instance-section-${idx}`}
                            data-section-tab={idx}
                            onClick={() => setActiveSection(idx)}
                            className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-[13px] mb-1 ${activeSection === idx
                                    ? "bg-[#FBEFE5] text-[#9F4212] font-semibold"
                                    : isHidden ? "text-[#AFAAA2] hover:bg-[#F5F3F0]" : "text-[#1F1F1F] hover:bg-[#F5F3F0]"
                                }`}>
                            <span className="w-5 h-5 grid place-items-center rounded text-[10px] font-bold text-white shrink-0"
                                style={{ background: isHidden ? "#AFAAA2" : (activeSection === idx ? "var(--brand-accent, #C75A1B)" : "#C0BCB4") }}>
                                {isHidden ? <EyeOff size={11} /> : (isFireCheckCat ? (numberById.get(s.id) || idx + 1) : idx + 1)}
                            </span>
                            <span className={`truncate flex-1 ${isHidden ? "italic" : ""}`}>{label}</span>
                            {showEmptyHint && (
                                <span data-testid={`instance-section-empty-${idx}`} title="This section won't appear in the final report — nothing has been captured yet."
                                    className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#D97706" }} />
                            )}
                        </button>
                        );
                    })}
                </div>

                {/* Active section */}
                <div className="bg-white border border-[#E5E0DA] rounded-2xl p-6 space-y-5">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg font-bold text-[#1F1F1F]">{secLabel}</h3>
                        <div className="flex items-center gap-2">
                            {canToggleSections && sec && !secIsHidden && isSectionHideable(sec) && (
                                <button data-testid={`section-hide-toggle-${activeSection}`}
                                    onClick={() => hideSectionNow(sec.id)} disabled={hideBusy}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64] hover:bg-[#F6F4F0] disabled:opacity-60">
                                    {hideBusy ? <Loader2 size={13} className="animate-spin" /> : <EyeOff size={13} />} Hide from report
                                </button>
                            )}
                            {sec?.title === "Executive Summary" && !readonly && (() => {
                                const esField = (sec.fields || []).find((f) => f.label === "Executive summary");
                                if (!esField) return null;
                                return (
                                    <button data-testid="instance-ai-summary" onClick={() => generateAiSummary(esField.id)} disabled={aiBusy}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold rounded-lg text-white disabled:opacity-60"
                                        style={{ background: "linear-gradient(90deg,#1F6FE5,#0EA5A5)" }}>
                                        {aiBusy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} AI draft summary
                                    </button>
                                );
                            })()}
                        </div>
                    </div>

                    {secIsHidden ? (
                        <div data-testid={`section-hidden-bar-${activeSection}`} className="rounded-xl px-4 py-6 text-center" style={{ background: "#F1EFEB" }}>
                            <p className="text-[13px] font-semibold text-[#6F6A64]">
                                Hidden from report{hiddenMap.get(sec.id)?.reason ? ` — ${hiddenMap.get(sec.id).reason}` : ""}
                            </p>
                            {canToggleSections && (
                                <button data-testid={`section-show-${activeSection}`} onClick={() => showSection(sec.id)}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold rounded-lg text-white"
                                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                    <Eye size={13} /> Show
                                </button>
                            )}
                        </div>
                    ) : (
                    <>
                    {(() => {
                        if (!BRANDING_CHOICE_CODES.has(template.code) || !sec) return null;
                        const secTitle = sec.title.trim().toLowerCase();
                        const hasFrontCover = (template.sections || []).some((s) => s.title.trim().toLowerCase() === "front cover");
                        const showBrandingCard = secTitle === "front cover" || (!hasFrontCover && activeSection === 0);
                        if (!showBrandingCard) return null;
                        return (
                        <div data-testid="report-branding-card" className="rounded-xl border border-[#E5E0DA] p-4" style={{ background: "#F9F7F4" }}>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9A968F] mb-2">Report branding</p>
                            {readonly ? (
                                <p className="text-[13px] text-[#1F1F1F]">
                                    {instance.report_branding === "priority_risk" ? "Priority Risk" : "Client Portal"}
                                </p>
                            ) : (
                                <div className="flex gap-2" data-testid="instance-branding-toggle">
                                    {[["client_portal", "Client Portal"], ["priority_risk", "Priority Risk"]].map(([val, lbl]) => (
                                        <button key={val} type="button" data-testid={`instance-branding-${val}`}
                                            onClick={() => updateReportBranding(val)}
                                            className="flex-1 px-3 py-2 rounded-lg text-[12.5px] font-semibold border transition-colors"
                                            style={(instance.report_branding || "client_portal") === val
                                                ? { background: "var(--brand-accent, #C75A1B)", color: "#fff", borderColor: "var(--brand-accent, #C75A1B)" }
                                                : { background: "#fff", color: "#6F6A64", borderColor: "#E5E0DA" }}>
                                            {lbl}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className="text-[10.5px] text-[#9A968F] mt-1.5">
                                {(instance.report_branding || "client_portal") === "priority_risk"
                                    ? "Uses Priority Risk's own colours on the cover and throughout the report."
                                    : "Uses this client's own brand colours and logo on the cover and throughout the report."}
                            </p>
                        </div>
                        );
                    })()}
                    {isFireCheckCat && sec && stripNumberPrefix(sec.title).trim().toLowerCase() === "inspection details" && (
                        <TimeEffortCard
                            timeStart={timeStart} timeEnd={timeEnd}
                            onChange={saveTime} readonly={readonly} fireCheck={true} />
                    )}
                    {FULL_REPORT_CODES.has(template.code) && sec && sec.title.trim().toLowerCase() !== "front cover"
                        && isSectionEmpty(sec, answers, instance.photos, comments) && (
                        <p data-testid="instance-section-empty-note" className="text-[12px] rounded-lg px-3 py-2"
                            style={{ background: "#FEF3E2", color: "#9A6400" }}>
                            This section won't appear in the final report — nothing has been captured here yet.
                        </p>
                    )}

                    {sec?.title === "Actions" ? (
                        <ActionsList actions={instanceActions} />
                    ) : (sec?.fields || []).filter((f) => {
                        if (!evaluateShowIf(f.show_if, answers)) return false;
                        if (f.field_type === "instruction" && f.internal_only && !isInternal) return false;
                        if (f.wsr_role !== "person_type_other") return true;
                        const ptField = (sec.fields || []).find((x) => x.wsr_role === "person_type");
                        return ptField && answers[ptField.id] === "Other";
                    }).map((f) => (
                        <div key={f.id} data-testid={`field-wrap-${f.id}`} data-field-id={f.id}>
                            <FieldRenderer field={f} value={answers[f.id]}
                                onChange={(v) => setAnswer(f.id, v)}
                                allAnswers={answers}
                                photos={(instance.photos || []).filter((p) => p.field_id === f.id)}
                                pendingPhotos={pendingPhotos.filter((p) => p.field_id === f.id)}
                                onPhoto={(file, rowId) => handlePhotoUpload(f.id, file, rowId)}
                                onUpdatePhoto={updatePhoto}
                                uploading={uploadingFor === f.id}
                                readonly={readonly}
                                comment={comments[f.id]}
                                onComment={(v) => setComment(f.id, v)}
                                actionCount={(instance.field_actions?.[f.id] || []).length}
                                fieldActions={instance.field_actions || {}}
                                instanceActions={instanceActions}
                                onRaiseAction={(rowId, rowLabel, meta) => setRaiseFor({
                                    fieldId: f.id, label: rowLabel ? `${f.label} — ${rowLabel}` : f.label, rowId,
                                    initialObservation: meta?.observation || "", initialTitle: meta?.title || rowLabel || f.label,
                                })}
                                onLinkAction={linkAction}
                                template={template}
                                fieldLabel={(instance.field_label_overrides || {})[f.id]}
                                onUpdateFieldLabel={(label) => updateFieldLabel(f.id, label)}
                                instanceId={id}
                                clientId={instance.client_id}
                                templateId={template?.id}
                                buildingId={instance.building_id}
                                staff={staff}
                                isInternal={isInternal}
                                dropdownLocked={!unlockedDropdowns.has(f.id)}
                                onUnlockDropdown={() => setUnlockedDropdowns((prev) => new Set(prev).add(f.id))}
                                onRetryPhoto={retryQueuedPhoto}
                                onDeletePhoto={deletePhoto}
                                onDiscardPhoto={discardQueuedPhoto}
                                sectionTestId={`instance-section-${activeSection}`} />
                        </div>
                    ))}

                    {sec && (() => {
                        const scoreField = (sec.fields || []).find((f) => f.field_type === "score");
                        const canEditActions = !readonly;
                        if (!scoreField || !canEditActions) return null;
                        const cnt = (instance.field_actions?.[scoreField.id] || []).length;
                        return (
                            <div className="pt-4 border-t border-[#F0EDE8] flex items-center justify-between gap-3 flex-wrap"
                                data-testid={`section-action-bar-${activeSection}`}>
                                <span className="text-[12px] text-[#6F6A64]">
                                    Raise an action for <b>{sec.title}</b> — assign an owner &amp; due date.
                                </span>
                                <button data-testid={`section-add-action-${activeSection}`}
                                    onClick={() => setRaiseFor({ fieldId: scoreField.id, label: sec.title })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                    <MessageSquarePlus size={13} /> Add action{cnt ? ` (${cnt})` : ""}
                                </button>
                            </div>
                        );
                    })()}

                    {sec && (
                        <SectionPhotos
                            sectionId={sec.id}
                            instanceId={id}
                            photos={(instance.photos || []).filter((p) => p.section_id === sec.id)}
                            pendingPhotos={pendingPhotos.filter((p) => p.section_id === sec.id)}
                            uploading={uploadingFor === `sec-${sec.id}`}
                            readonly={readonly}
                            onUpload={(file) => handleSectionPhotoUpload(sec.id, file)}
                            onUpdate={updatePhoto}
                            onReorder={reorderPhotos}
                            onRetryPhoto={retryQueuedPhoto}
                            onDiscardPhoto={discardQueuedPhoto}
                            onDelete={deletePhoto} />
                    )}
                    </>
                    )}

                    {activeSection === navSections.length - 1 && template.requires_signature && (
                        <div className="pt-4 border-t border-[#F0EDE8]">
                            <SignaturePad value={signature}
                                onChange={handleSignatureChange}
                                disabled={readonly}
                                label="Inspector signature" />
                        </div>
                    )}

                    <div data-help-avoid="true" className="flex items-center justify-between pt-4 border-t border-[#F0EDE8]">
                        <button data-testid="section-prev" disabled={activeSection === 0}
                            onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#6F6A64] hover:text-[#1F1F1F] disabled:opacity-30">
                            <ChevronLeft size={14} /> Previous
                        </button>
                        <span className="text-[11px] text-[#9A968F]">
                            Section {activeSection + 1} / {navSections.length}
                        </span>
                        <button data-testid="section-next"
                            disabled={activeSection === navSections.length - 1}
                            onClick={() => setActiveSection((s) => Math.min(navSections.length - 1, s + 1))}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#1F1F1F] hover:text-[var(--brand-accent,_#C75A1B)] disabled:opacity-30">
                            Next <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
            {raiseFor && (
                <RaiseActionDialog label={raiseFor.label} initialObservation={raiseFor.initialObservation}
                    onClose={() => setRaiseFor(null)}
                    onSubmit={(payload) => submitAction(raiseFor.fieldId, payload, raiseFor.rowId, raiseFor.initialTitle)} />
            )}
            {emailOpen && (
                <EmailReportDialog target={{ kind: "instance", id, title: instance.title || template.name }}
                    onClose={() => setEmailOpen(false)} />
            )}
        </div>
    );
}

function durationMinutes(startIso, endIso) {
    if (!startIso || !endIso) return null;
    const mins = Math.round((new Date(endIso) - new Date(startIso)) / 60000);
    return isNaN(mins) ? null : mins;
}

function fmtDuration(startIso, endIso) {
    const mins = durationMinutes(startIso, endIso);
    if (mins == null || mins < 0) return null;
    const h = Math.floor(mins / 60), m = mins % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
}

function TimeEffortCard({ timeStart, timeEnd, onChange, readonly, fireCheck }) {
    const dur = fmtDuration(timeStart, timeEnd);
    const mins = durationMinutes(timeStart, timeEnd);
    const overLimit = fireCheck && mins != null && mins > 720;

    // Local drafts: onChange only updates the draft while typing (native
    // datetime-local fires onChange on every keystroke/segment, so validating
    // there rejects legitimate in-progress edits like "01:30" -> "11:30").
    // Commit (convert + validate + save) happens on blur for fire checks.
    const draftOf = (iso) => (fireCheck ? isoToLocalInput(iso) : (iso || "").slice(0, 16));
    const [startDraft, setStartDraft] = useState(() => draftOf(timeStart));
    const [endDraft, setEndDraft] = useState(() => draftOf(timeEnd));
    useEffect(() => setStartDraft(draftOf(timeStart)), [timeStart, fireCheck]);
    useEffect(() => setEndDraft(draftOf(timeEnd)), [timeEnd, fireCheck]);

    if (!fireCheck) {
        return (
            <div data-testid="time-effort-card" className="bg-white border border-[#E5E0DA] rounded-2xl p-4 flex flex-wrap items-end gap-4">
                <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-[#6F6A64] w-full sm:w-auto">
                    <Clock size={14} /> Time &amp; effort <span className="text-[10px] font-semibold text-[#9A968F] normal-case">· PR internal only</span>
                </div>
                <div>
                    <label className="block text-[12px] font-semibold text-[#1F1F1F] mb-1">Started</label>
                    <input type="datetime-local" data-testid="time-started-input" disabled={readonly}
                        value={startDraft} onChange={(e) => { setStartDraft(e.target.value); onChange("time_started", e.target.value); }}
                        className={inputCls + " w-56"} />
                </div>
                <div>
                    <label className="block text-[12px] font-semibold text-[#1F1F1F] mb-1">Ended</label>
                    <input type="datetime-local" data-testid="time-ended-input" disabled={readonly}
                        value={endDraft} onChange={(e) => { setEndDraft(e.target.value); onChange("time_ended", e.target.value); }}
                        className={inputCls + " w-56"} />
                </div>
                <div className="ml-auto text-right">
                    <span className="block text-[11px] text-[#9A968F]">Time taken</span>
                    <span data-testid="time-taken-value" className="text-[15px] font-bold text-[#1F1F1F]">{dur || "—"}</span>
                </div>
            </div>
        );
    }

    const commitStart = () => {
        const iso = startDraft ? localInputToLondonIso(startDraft) : "";
        if (iso && timeEnd && new Date(iso) >= new Date(timeEnd)) {
            toast.error("Started must be before Finished");
            setStartDraft(draftOf(timeStart));
            return;
        }
        onChange("time_started", iso);
    };
    const commitEnd = () => {
        const iso = endDraft ? localInputToLondonIso(endDraft) : "";
        if (iso && timeStart && new Date(iso) <= new Date(timeStart)) {
            toast.error("Finished must be after Started");
            setEndDraft(draftOf(timeEnd));
            return;
        }
        onChange("time_ended", iso);
    };
    const finishNow = () => {
        const iso = nowLondonIso();
        if (timeStart && new Date(iso) <= new Date(timeStart)) {
            toast.error("Finished must be after Started");
            return;
        }
        setEndDraft(draftOf(iso));
        onChange("time_ended", iso);
    };

    return (
        <div data-testid="time-effort-card" className="bg-white border border-[#E5E0DA] rounded-2xl p-4 flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-[#6F6A64] w-full sm:w-auto">
                <Clock size={14} /> Inspection start / finish
            </div>
            <div>
                <label className="block text-[12px] font-semibold text-[#1F1F1F] mb-1">Started</label>
                <input type="datetime-local" data-testid="time-started-input" disabled={readonly}
                    value={startDraft} onChange={(e) => setStartDraft(e.target.value)} onBlur={commitStart}
                    className={inputCls + " w-56"} />
            </div>
            <div>
                <label className="block text-[12px] font-semibold text-[#1F1F1F] mb-1">Finished</label>
                <div className="flex items-center gap-2">
                    <input type="datetime-local" data-testid="time-ended-input" disabled={readonly}
                        value={endDraft} onChange={(e) => setEndDraft(e.target.value)} onBlur={commitEnd}
                        className={inputCls + " w-56"} />
                    {!readonly && (
                        <button type="button" data-testid="time-finish-now-btn" onClick={finishNow}
                            className="text-[11px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline shrink-0">
                            Finish now
                        </button>
                    )}
                </div>
            </div>
            <div className="ml-auto text-right">
                <span className="block text-[11px] text-[#9A968F]">Duration</span>
                <span data-testid="time-taken-value" className="text-[15px] font-bold text-[#1F1F1F]">{dur || "—"}</span>
                {overLimit && (
                    <p data-testid="time-over-limit-warning" className="text-[10px] font-semibold mt-0.5" style={{ color: "#B35A00" }}>
                        Over 12 hours — please check
                    </p>
                )}
            </div>
        </div>
    );
}

// Thumbnail strip for photos not yet confirmed on the server — either queued
// while offline (dashed gold border) or permanently failed (red border, tap
// to retry) so nothing silently vanishes. Reused by the inline yes_no photo
// picker and the photo field type.
// Thumbnail strip for photos not yet confirmed on the server — either queued
// while offline (dashed gold border) or permanently failed (red border, tap
// to retry) so nothing silently vanishes. Reused by the inline yes_no photo
// picker and the photo field type.
function GridCellControl({ col, value, onChange, disabled, hint, big }) {
    const isFault = value != null && value !== "" && (col.fault_values || []).includes(value);
    if (col.type === "yes_no" || col.type === "open_closed") {
        const opts = col.type === "yes_no" ? ["Yes", "No"] : ["Open", "Closed"];
        if (col.na_allowed !== false) opts.push("N/A");
        if (big) {
            return (
                <div className="flex gap-1.5 flex-wrap">
                    {opts.map((o) => {
                        const sel = value === o;
                        const fault = (col.fault_values || []).includes(o);
                        return (
                            <button key={o} type="button" disabled={disabled} onClick={() => onChange(o)}
                                data-testid={`grid-cell-${col.id}-${o.replace(/\W/g, "")}`}
                                className={`px-3.5 py-2 rounded-lg border text-[13px] font-semibold ${sel ? "text-white" : "bg-white text-[#1F1F1F]"}`}
                                style={{ borderColor: sel ? (fault ? "#B33A2E" : "#3F7D3A") : "#E5E0DA", background: sel ? (fault ? "#B33A2E" : "#3F7D3A") : undefined }}>
                                {o}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return (
            <select disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
                className={`h-8 px-1.5 rounded border text-[12px] w-full ${isFault ? "bg-[#FBE9E7] border-[#E7B3AC] text-[#8A2C22] font-semibold" : "border-[#E5E0DA]"}`}>
                <option value="">—</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }
    if (col.type === "select") {
        const opts = [...(col.options || [])];
        // A previously-carried-forward or stale value that's no longer in the
        // building's configured list must stay visible/selectable — never
        // silently drop saved data from the UI (the PDF/DOCX always print it).
        if (value && !opts.includes(value)) opts.push(value);
        if (col.na_allowed !== false) opts.push("N/A");
        if (big) {
            return (
                <div className="flex gap-1.5 flex-wrap">
                    {opts.map((o) => {
                        const sel = value === o; const fault = (col.fault_values || []).includes(o);
                        return (
                            <button key={o} type="button" disabled={disabled} onClick={() => onChange(o)}
                                className={`px-3 py-2 rounded-lg border text-[12.5px] font-semibold ${sel ? "text-white" : "bg-white text-[#1F1F1F]"}`}
                                style={{ borderColor: sel ? (fault ? "#B33A2E" : "#3F7D3A") : "#E5E0DA", background: sel ? (fault ? "#B33A2E" : "#3F7D3A") : undefined }}>
                                {o}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return (
            <select disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
                className={`h-8 px-1.5 rounded border text-[12px] w-full ${isFault ? "bg-[#FBE9E7] border-[#E7B3AC] text-[#8A2C22] font-semibold" : "border-[#E5E0DA]"}`}>
                <option value="">—</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }
    if (col.type === "number") {
        return (
            <div>
                <input type="number" inputMode="decimal" disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                    className={`${big ? "h-11 text-[15px] w-full" : "h-8 w-24 text-[12px]"} px-2 rounded border ${isFault ? "bg-[#FBE9E7] border-[#E7B3AC]" : "border-[#E5E0DA]"}`} />
                {hint != null && hint !== "" && <p className="text-[10px] text-[#9A968F] mt-0.5">last: {hint}</p>}
            </div>
        );
    }
    if (col.type === "date") {
        return (
            <input type="date" disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
                className={`${big ? "h-11 text-[15px] w-full" : "h-8 w-36 text-[12px]"} px-2 rounded border ${isFault ? "bg-[#FBE9E7] border-[#E7B3AC]" : "border-[#E5E0DA]"}`} />
        );
    }
    return (
        <input type="text" disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
            className={`${big ? "h-10 text-[13px] w-full" : "h-8 w-44 text-[12px]"} px-2 rounded border border-[#E5E0DA]`} />
    );
}

// New Stairwell vents/dampers rows only — composes a "Floor N - L1/L2 -
// Ref" string (Ref optional) into the grid's existing "Floor / Head No." id
// column; "Other" swaps to a free-text location for dampers/windows/etc.
// Preloaded template rows are untouched — only shown for user-added rows.
const FLOOR_OPTIONS = ["0", "1", "2", "3", "4", "5", "6", "7", "8"];
function FloorHeadCell({ value, onChange, disabled }) {
    const m = /^Floor (\d) - (L1|L2)(?: - (.*))?$/.exec(value || "");
    const m2 = !m && /^Floor (\d)$/.exec(value || "");
    const parsedFloor = m ? m[1] : (m2 ? m2[1] : "");
    const head = m ? m[2] : "";
    const ref = m ? (m[3] || "") : "";
    // "Other" mode must survive an empty text box (compose("Other", "", "") would
    // otherwise collapse to "" and be indistinguishable from "nothing picked yet"),
    // so it's tracked in local state, seeded from the saved value on first mount.
    const [otherMode, setOtherMode] = useState(() => !m && !m2 && !!value);
    const floor = otherMode ? "Other" : parsedFloor;
    const other = otherMode ? (value || "") : "";
    const compose = (f, h, r) => {
        if (!f) return "";
        let s = `Floor ${f}`;
        if (h) s += ` - ${h}`;
        if (r) s += ` - ${r}`;
        return s;
    };
    const onFloorChange = (v) => {
        if (v === "Other") {
            setOtherMode(true);
            onChange("");
            return;
        }
        setOtherMode(false);
        onChange(compose(v, head, ref));
    };
    const selCls = "h-8 px-1.5 rounded border border-[#E5E0DA] text-[12px] disabled:bg-transparent";
    return (
        <div className="flex flex-wrap gap-1 items-center">
            <select data-testid="floor-head-floor-select" className={selCls} value={floor} disabled={disabled}
                onChange={(e) => onFloorChange(e.target.value)}>
                <option value="">Floor…</option>
                {FLOOR_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                <option value="Other">Other…</option>
            </select>
            {otherMode ? (
                <input data-testid="floor-head-other-input" className={selCls + " w-40"} placeholder="Location…" value={other} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)} />
            ) : (
                <>
                    <select data-testid="floor-head-head-select" className={selCls} value={head} disabled={disabled}
                        onChange={(e) => onChange(compose(floor, e.target.value, ref))}>
                        <option value="">Head…</option>
                        <option value="L1">L1</option>
                        <option value="L2">L2</option>
                    </select>
                    <input data-testid="floor-head-ref-input" className={selCls + " w-24"} placeholder="Ref (optional)" value={ref} disabled={disabled}
                        onChange={(e) => onChange(compose(floor, head, e.target.value))} />
                </>
            )}
        </div>
    );
}

// Fire-check-sheet style table: fixed asset rows (door/riser/damper…) with the
// same checks on every row. Table on desktop/tablet, one big-touch card per
// row on phones. A fault highlights the row and (optionally) offers remarks,
// a tagged photo and "Raise action" carrying the row_id.
function CheckGridField({ field, value, onChange, photos, pendingPhotos, onPhoto, uploading, readonly,
                         fieldActions = {}, onRaiseAction, instanceId, templateId, buildingId, allAnswers,
                         onRetryPhoto, onDeletePhoto, onDiscardPhoto }) {
    const grid = field.grid || { id_columns: [], check_columns: [], rows: [], allow_add_rows: false };
    const idCols = grid.id_columns || [];
    const gridAnswers = (value && typeof value === "object" && !Array.isArray(value)) ? value : {};
    const extraRows = gridAnswers._extra_rows || [];
    const allRows = effectiveGridRows(grid, gridAnswers, allAnswers);
    const [hints, setHints] = useState({});
    const rowToggleEnabled = !!grid.row_toggle_enabled;
    // A brand-new instance created without a Building yet (so carry-forward never
    // ran) has no `_inactive_rows` key at all — default every row to inactive
    // there too, same as a normal carried/first-time creation. Once the
    // inspector has toggled anything the key becomes an explicit array (even
    // empty) and this default no longer applies.
    const inactiveSet = new Set(
        gridAnswers._inactive_rows !== undefined ? gridAnswers._inactive_rows : (rowToggleEnabled ? allRows.map((r) => r.id) : [])
    );
    const isRowInactive = (rowId) => rowToggleEnabled && inactiveSet.has(rowId);
    const toggleRowActive = (rowId) => {
        const next = new Set(inactiveSet);
        if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
        onChange({ ...gridAnswers, _inactive_rows: Array.from(next) });
    };
    const [buildingStairwells, setBuildingStairwells] = useState(null);
    const hasDynamicStairCol = (grid.check_columns || []).some((c) => c.dynamic_options_source === "building_stairwells");
    useEffect(() => {
        if (!hasDynamicStairCol || !buildingId) return;
        api.get(`/buildings/${buildingId}`).then(({ data }) => setBuildingStairwells(data.stairwells || [])).catch(() => setBuildingStairwells([]));
    }, [hasDynamicStairCol, buildingId]);
    const chkCols = (grid.check_columns || []).map((c) => (
        c.dynamic_options_source === "building_stairwells" && buildingStairwells?.length
            ? { ...c, options: buildingStairwells } : c
    ));

    useEffect(() => {
        if (!buildingId || !templateId) return;
        if (!chkCols.some((c) => c.type === "number")) return;
        api.get("/forms/instances/grid-hints", { params: { template_id: templateId, building_id: buildingId, field_id: field.id, exclude_instance_id: instanceId } })
            .then((r) => setHints(r.data || {})).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, buildingId, field.id, instanceId]);

    const setCell = (rowId, colId, v) => onChange({ ...gridAnswers, [rowId]: { ...(gridAnswers[rowId] || {}), [colId]: v } });
    const addExtraRow = () => onChange({ ...gridAnswers, _extra_rows: [...extraRows, { id: `extra-${Date.now()}`, id_values: {} }] });
    const removeExtraRow = (rowId) => {
        const rest = { ...gridAnswers };
        delete rest[rowId];
        onChange({ ...rest, _extra_rows: extraRows.filter((r) => r.id !== rowId) });
    };
    const setExtraRowIdValue = (rowId, colId, v) =>
        onChange({ ...gridAnswers, _extra_rows: extraRows.map((r) => (r.id === rowId ? { ...r, id_values: { ...r.id_values, [colId]: v } } : r)) });
    const setRowOverride = (rowId, colId, v) => onChange({
        ...gridAnswers,
        _row_overrides: { ...(gridAnswers._row_overrides || {}), [rowId]: { ...((gridAnswers._row_overrides || {})[rowId] || {}), [colId]: v } },
    });
    // Removing a configured (template) row only hides it from THIS inspection and
    // its PDF via `_removed_rows` — the master template and other inspections are
    // untouched. Removing an extra (manually added) row deletes it outright, same
    // as before, since it only ever existed on this instance.
    const removeRow = (row) => {
        const isExtra = extraRows.some((r) => r.id === row.id);
        const rowLabel = idCols.map((c) => row.id_values?.[c.id]).filter(Boolean).join(" / ") || row.id;
        if (!window.confirm(`Remove "${rowLabel}" from this inspection? It won't appear in this report or its PDF.${isExtra ? "" : " This does not affect the master template or any other inspection."}`)) return;
        if (isExtra) { removeExtraRow(row.id); return; }
        const rest = { ...gridAnswers };
        delete rest[row.id];
        const removed = new Set(gridAnswers._removed_rows || []);
        removed.add(row.id);
        onChange({ ...rest, _removed_rows: Array.from(removed) });
    };

    const isRowFault = (row) => {
        const na = new Set(row.na_columns || []);
        return chkCols.some((c) => !na.has(c.id) && (gridAnswers[row.id] || {})[c.id] && (c.fault_values || []).includes((gridAnswers[row.id] || {})[c.id]));
    };
    // Rows toggled inactive this week don't count towards completeness/faults
    // or the exported report (row_toggle_enabled grids only).
    const activeRows = rowToggleEnabled ? allRows.filter((r) => !isRowInactive(r.id)) : allRows;
    // Free-text columns (e.g. "Remarks") are always optional annotations, never
    // the actual check result — they don't count towards row completeness.
    const completeCount = activeRows.filter((r) => {
        const na = new Set(r.na_columns || []);
        return chkCols.every((c) => na.has(c.id) || c.type === "text" || !!(gridAnswers[r.id] || {})[c.id]);
    }).length;
    const faultRows = activeRows.filter(isRowFault);

    const RowIssuePanel = ({ row }) => {
        const rowLabel = idCols.map((c) => row.id_values?.[c.id]).filter(Boolean).join(" / ") || row.id;
        const actionKey = `${field.id}:${row.id}`;
        const count = (fieldActions[actionKey] || []).length;
        return (
            <div className="rounded-lg border border-[#F3C4BC] bg-[#FBE9E7] p-3 space-y-2" data-testid={`grid-issue-${row.id}`}>
                <p className="text-[12.5px] font-semibold text-[#8A2C22] flex items-center gap-1.5"><AlertTriangle size={13} /> {rowLabel}</p>
                <input data-testid={`grid-row-remarks-${row.id}`} value={(gridAnswers[row.id] || {})._remarks || ""} disabled={readonly}
                    placeholder="Remarks (optional)…" onChange={(e) => setCell(row.id, "_remarks", e.target.value)}
                    className="w-full h-8 px-2.5 text-[12.5px] rounded-lg border border-[#E7B3AC] bg-white outline-none" />
                {!readonly && onRaiseAction && (
                    <button type="button" data-testid={`grid-row-raise-action-${row.id}`} onClick={() => onRaiseAction(row.id, rowLabel)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border border-[#B33A2E] text-white" style={{ background: "#B33A2E" }}>
                        <MessageSquarePlus size={12} /> Raise action{count ? ` (${count})` : ""}
                    </button>
                )}
            </div>
        );
    };

    // Optional photo on every row, regardless of pass/fail — a compact camera
    // button plus whatever's already attached/queued for this row.
    const RowPhotoInline = ({ row, big }) => {
        const rowPhotos = (photos || []).filter((p) => p.row_id === row.id);
        const rowPending = (pendingPhotos || []).filter((p) => p.row_id === row.id);
        const size = big ? "w-9 h-9" : "w-7 h-7";
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {!readonly && onPhoto && (
                    <label className={`inline-flex items-center justify-center ${size} rounded-lg border border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)] cursor-pointer shrink-0`}
                        data-testid={`grid-row-photo-${row.id}`} title="Add photo">
                        {uploading ? <Loader2 className="animate-spin" size={big ? 14 : 12} /> : <Camera size={big ? 14 : 12} />}
                        <input type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => { onPhoto(e.target.files, row.id); e.target.value = ""; }} />
                    </label>
                )}
                {rowPhotos.map((p) => (
                    <div key={p.id} className={`relative ${size} shrink-0`}>
                        <PhotoImg testId={`grid-row-photo-img-${p.id}`} src={`${API}/forms/instances/${instanceId}/photos/${p.id}/file`} alt={p.filename}
                            className={`${size} object-cover rounded border border-[#E5E0DA]`} />
                        {!readonly && onDeletePhoto && (
                            <button type="button" onClick={() => { if (window.confirm("Remove this photo?")) onDeletePhoto(p.id); }}
                                className="absolute -top-1 -right-1 w-3.5 h-3.5 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]"><X size={8} /></button>
                        )}
                    </div>
                ))}
                <PendingPhotoThumbs pending={rowPending} testId={`grid-row-pending-${row.id}`} onRetry={onRetryPhoto} onDiscard={readonly ? null : onDiscardPhoto} />
            </div>
        );
    };

    return (
        <div data-testid={`field-wrap-${field.id}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                <label className="block text-[13px] font-semibold text-[#1F1F1F]">
                    {field.label}{field.required && <span className="text-red-600 ml-1">*</span>}
                </label>
                <span data-testid={`field-${field.id}-progress`} className="text-[11.5px] font-semibold text-[#6F6A64]">
                    {completeCount} / {activeRows.length} rows complete
                </span>
            </div>

            {/* Desktop / tablet table */}
            <div className="hidden sm:block overflow-x-auto border border-[#E5E0DA] rounded-lg" data-testid={`field-${field.id}-table`}>
                <table className="w-full text-[12.5px]">
                    <thead>
                        <tr className="bg-[#F6F4F0]">
                            {idCols.map((c) => <th key={c.id} className="text-left px-2.5 py-2 font-semibold text-[#6F6A64] whitespace-nowrap">{c.label}</th>)}
                            {chkCols.map((c) => (
                                <th key={c.id} className="text-left px-2.5 py-2 font-semibold text-[#6F6A64] whitespace-nowrap">
                                    {c.label}{c.unit ? ` (${c.unit})` : ""}{c.required ? <span className="text-red-600">*</span> : ""}
                                </th>
                            ))}
                            <th className="text-left px-2.5 py-2 font-semibold text-[#6F6A64] whitespace-nowrap">Photo</th>
                            {rowToggleEnabled && <th className="w-8" />}
                            {!readonly && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {allRows.map((row) => {
                            const isExtra = extraRows.some((r) => r.id === row.id);
                            const na = new Set(row.na_columns || []);
                            const rowAns = gridAnswers[row.id] || {};
                            const fault = isRowFault(row);
                            const inactive = isRowInactive(row.id);
                            return (
                                <tr key={row.id} data-testid={`grid-row-${row.id}`}
                                    className={inactive ? "bg-[#F6F4F0] opacity-60" : (fault ? "bg-[#FBE9E7]" : "")}>
                                    {idCols.map((c) => (
                                        <td key={c.id} className="px-2.5 py-1.5 align-top">
                                            {isExtra && c.label === "Floor / Head No." ? (
                                                <FloorHeadCell value={row.id_values?.[c.id] || ""} disabled={readonly}
                                                    onChange={(v) => setExtraRowIdValue(row.id, c.id, v)} />
                                            ) : (
                                                <input type={c.type === "date" ? "date" : "text"} value={row.id_values?.[c.id] || ""} disabled={readonly} placeholder="—"
                                                    onChange={(e) => (isExtra ? setExtraRowIdValue : setRowOverride)(row.id, c.id, e.target.value)}
                                                    className={`h-8 ${c.type === "date" ? "w-36" : "w-56"} px-1.5 rounded border border-[#E5E0DA] text-[12px] disabled:border-transparent disabled:bg-transparent disabled:px-0`} />
                                            )}
                                        </td>
                                    ))}
                                    {chkCols.map((c) => (
                                        <td key={c.id} className="px-2.5 py-1.5 align-top">
                                            {na.has(c.id) ? (
                                                <span className="inline-block px-2 py-1 rounded bg-[#E5E0DA] text-[#6F6A64] text-[11px] font-semibold">N/A</span>
                                            ) : (
                                                <GridCellControl col={c} value={rowAns[c.id]} disabled={readonly}
                                                    onChange={(v) => setCell(row.id, c.id, v)}
                                                    hint={c.type === "number" ? hints[row.id]?.[c.id] : undefined} />
                                            )}
                                        </td>
                                    ))}
                                    <td className="px-2.5 py-1.5 align-top"><RowPhotoInline row={row} /></td>
                                    {rowToggleEnabled && (
                                        <td className="px-2 py-1.5">
                                            <button onClick={() => toggleRowActive(row.id)} disabled={readonly}
                                                data-testid={`grid-row-toggle-${row.id}`} title={inactive ? "Activate this row for this week's check" : "Not being tested this week — hide"}
                                                className="p-1 rounded hover:bg-[#F0EDE8] text-[#9A968F] hover:text-[#1F1F1F] disabled:opacity-40">
                                                {inactive ? <EyeOff size={13} /> : <Eye size={13} />}
                                            </button>
                                        </td>
                                    )}
                                    {!readonly && (
                                        <td className="px-2 py-1.5">
                                            <button onClick={() => removeRow(row)} data-testid={`grid-row-remove-${row.id}`} title="Remove from this inspection" className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {allRows.length === 0 && <p className="text-[11px] text-[#9A968F] italic p-3">No rows configured for this grid.</p>}
            </div>
            {faultRows.length > 0 && (
                <div className="hidden sm:block mt-2 space-y-2">
                    {faultRows.map((row) => <RowIssuePanel key={row.id} row={row} />)}
                </div>
            )}

            {/* Mobile: one big-touch card per row */}
            <div className="sm:hidden space-y-3" data-testid={`field-${field.id}-cards`}>
                {allRows.map((row) => {
                    const isExtra = extraRows.some((r) => r.id === row.id);
                    const na = new Set(row.na_columns || []);
                    const rowAns = gridAnswers[row.id] || {};
                    const fault = isRowFault(row);
                    const inactive = isRowInactive(row.id);
                    const rowLabel = idCols.map((c) => row.id_values?.[c.id]).filter(Boolean).join(" / ") || row.id;
                    return (
                        <div key={row.id} data-testid={`grid-card-${row.id}`}
                            className={`rounded-xl border p-3 space-y-2.5 ${inactive ? "border-[#E5E0DA] bg-[#F6F4F0] opacity-60" : (fault ? "border-[#E7B3AC] bg-[#FBE9E7]" : "border-[#E5E0DA] bg-white")}`}>
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5 flex-1">
                                    {idCols.map((c) => (
                                        isExtra && c.label === "Floor / Head No." ? (
                                            <FloorHeadCell key={c.id} value={row.id_values?.[c.id] || ""} disabled={readonly}
                                                onChange={(v) => setExtraRowIdValue(row.id, c.id, v)} />
                                        ) : (
                                            <input key={c.id} type={c.type === "date" ? "date" : "text"} value={row.id_values?.[c.id] || ""} disabled={readonly} placeholder={c.label}
                                                onChange={(e) => (isExtra ? setExtraRowIdValue : setRowOverride)(row.id, c.id, e.target.value)}
                                                className="block h-8 w-full px-2 mb-1 rounded border border-[#E5E0DA] text-[12.5px] disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:font-semibold disabled:text-[13px] disabled:text-[#1F1F1F]" />
                                        )
                                    ))}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {rowToggleEnabled && (
                                        <button onClick={() => toggleRowActive(row.id)} disabled={readonly}
                                            data-testid={`grid-card-toggle-${row.id}`} title={inactive ? "Activate this row for this week's check" : "Not being tested this week — hide"}
                                            className="p-1 rounded hover:bg-[#F0EDE8] text-[#9A968F] hover:text-[#1F1F1F] disabled:opacity-40">
                                            {inactive ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </button>
                                    )}
                                    {!readonly && <button onClick={() => removeRow(row)} data-testid={`grid-card-remove-${row.id}`} title="Remove from this inspection" className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={14} /></button>}
                                </div>
                            </div>
                            <div className="space-y-2">
                                {chkCols.map((c) => (
                                    <div key={c.id}>
                                        <p className="text-[11px] font-semibold text-[#6F6A64] mb-1">{c.label}{c.unit ? ` (${c.unit})` : ""}{c.required ? <span className="text-red-600">*</span> : ""}</p>
                                        {na.has(c.id) ? (
                                            <span className="inline-block px-2.5 py-1.5 rounded-lg bg-[#E5E0DA] text-[#6F6A64] text-[12px] font-semibold">N/A</span>
                                        ) : (
                                            <GridCellControl col={c} value={rowAns[c.id]} disabled={readonly} big
                                                onChange={(v) => setCell(row.id, c.id, v)}
                                                hint={c.type === "number" ? hints[row.id]?.[c.id] : undefined} />
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold text-[#6F6A64] mb-1">Photo</p>
                                <RowPhotoInline row={row} big />
                            </div>
                            {fault && <RowIssuePanel row={row} />}
                        </div>
                    );
                })}
                {allRows.length === 0 && <p className="text-[11px] text-[#9A968F] italic">No rows configured for this grid.</p>}
            </div>

            {grid.allow_add_rows && !readonly && (
                <button type="button" data-testid={`field-${field.id}-add-row`} onClick={addExtraRow}
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline">
                    + Add row
                </button>
            )}
        </div>
    );
}

function FieldRenderer({ field, value, onChange, photos, pendingPhotos = [], onPhoto, onUpdatePhoto, uploading, readonly, sectionTestId,
                        comment, onComment, actionCount = 0, fieldActions = {}, instanceActions = [], onRaiseAction, onLinkAction,
                        template, fieldLabel, onUpdateFieldLabel, instanceId, templateId, buildingId, clientId,
                        staff = [], onRetryPhoto, onDeletePhoto, onDiscardPhoto, allAnswers, isInternal,
                        dropdownLocked, onUnlockDropdown }) {
    const ftype = field.field_type;
    const [dragOver, setDragOver] = useState(false);
    const textareaRef = useRef(null);
    useEffect(() => {
        if (ftype !== "textarea" || !textareaRef.current) return;
        const el = textareaRef.current;
        const maxH = Math.round(window.innerHeight * 0.5);
        el.style.height = "auto";
        const needed = el.scrollHeight;
        if (needed > maxH) {
            el.style.height = `${maxH}px`;
            el.style.overflowY = "auto";
        } else {
            el.style.height = `${needed}px`;
            el.style.overflowY = "hidden";
        }
    }, [value, ftype]);
    const labelEl = (
        <label className="block text-[13px] font-semibold text-[#1F1F1F] mb-1.5">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
        </label>
    );
    const helpEl = field.help_text ? <p className="text-[11px] text-[#9A968F] mt-1">{field.help_text}</p> : null;

    if (ftype === "instruction") {
        return (
            <div className="rounded-lg bg-[#F5F3F0] border-l-4 border-[var(--brand-accent,_#C75A1B)] px-4 py-3">
                <p className="text-[13px] text-[#1F1F1F]">{field.content || field.label}</p>
            </div>
        );
    }

    if (ftype === "check_grid") {
        return (
            <CheckGridField field={field} value={value} onChange={onChange}
                photos={photos} pendingPhotos={pendingPhotos} onPhoto={onPhoto} uploading={uploading} readonly={readonly}
                fieldActions={fieldActions} onRaiseAction={onRaiseAction} instanceId={instanceId}
                templateId={templateId} buildingId={buildingId} allAnswers={allAnswers}
                onRetryPhoto={onRetryPhoto} onDeletePhoto={onDeletePhoto} onDiscardPhoto={onDiscardPhoto} />
        );
    }

    if (ftype === "text") {
        return (
            <div>
                {labelEl}
                <input data-testid={`field-${field.id}`}
                    value={value || ""} disabled={readonly}
                    placeholder={field.placeholder}
                    onChange={(e) => onChange(e.target.value)} className={inputCls} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "textarea") {
        return (
            <div>
                {labelEl}
                <textarea data-testid={`field-${field.id}`}
                    ref={textareaRef}
                    rows={4} value={value || ""} disabled={readonly}
                    placeholder={field.placeholder}
                    onChange={(e) => onChange(e.target.value)} className={inputCls + " resize-none overflow-hidden"}
                    style={{ minHeight: "6rem" }} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "number") {
        return (
            <div>
                {labelEl}
                <input type="number" data-testid={`field-${field.id}`}
                    value={value ?? ""} disabled={readonly}
                    placeholder={field.placeholder}
                    onChange={(e) => onChange(e.target.value)} className={inputCls} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "date") {
        return (
            <div>
                {labelEl}
                <input type="date" data-testid={`field-${field.id}`}
                    value={value || ""} disabled={readonly}
                    onChange={(e) => onChange(e.target.value)} className={inputCls + " w-48"} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "time") {
        return (
            <div>
                {labelEl}
                <div className="flex items-center gap-2">
                    <input type="time" data-testid={`field-${field.id}`}
                        value={value || ""} disabled={readonly}
                        onChange={(e) => onChange(e.target.value)} className={inputCls + " w-32"} />
                    {!readonly && (
                        <button type="button" data-testid={`field-${field.id}-now`} onClick={() => onChange(nowLondonHHMM())}
                            className="text-[11px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline shrink-0">Now</button>
                    )}
                </div>
                {helpEl}
            </div>
        );
    }
    if (ftype === "yes_no") {
        const isNo = (value || "").toLowerCase() === "no";
        return (
            <div className="pb-1 border-b border-[#F4F1EC]" data-testid={`field-wrap-${field.id}`}>
                {labelEl}
                <div className="flex gap-2 flex-wrap items-center" data-testid={`field-${field.id}`}>
                    {[
                        { v: "yes", l: "Yes", c: "#3F7D3A" },
                        { v: "no", l: "No", c: "#B33A2E" },
                        { v: "na", l: "N/A", c: "#6F6A64" },
                    ].map((opt) => {
                        const sel = (value || "").toLowerCase() === opt.v;
                        return (
                            <button key={opt.v} type="button" disabled={readonly}
                                data-testid={`field-${field.id}-${opt.v}`}
                                onClick={() => onChange(opt.v)}
                                className={`px-4 py-1.5 text-sm font-semibold rounded-lg border transition-all ${sel ? "text-white" : "text-[#1F1F1F] bg-white"
                                    }`}
                                style={{
                                    borderColor: sel ? opt.c : "#E5E0DA",
                                    background: sel ? opt.c : undefined,
                                }}>
                                {opt.l}
                            </button>
                        );
                    })}
                    {!readonly && onPhoto && (
                        <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)] cursor-pointer text-[12px] font-semibold">
                            {uploading ? <Loader2 className="animate-spin" size={12} /> : <Camera size={12} />} Photo
                            <input type="file" accept="image/*" multiple className="hidden"
                                data-testid={`field-${field.id}-photo`}
                                onChange={(e) => { onPhoto(e.target.files); e.target.value = ""; }} />
                        </label>
                    )}
                    {!readonly && onRaiseAction && (
                        <button type="button" data-testid={`field-${field.id}-raise-action`} onClick={() => onRaiseAction()}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${isNo ? "text-white border-[#B33A2E]" : "text-[#1F1F1F] bg-white border-[#E5E0DA] hover:bg-[#F6F4F0]"}`}
                            style={isNo ? { background: "#B33A2E" } : undefined}>
                            <MessageSquarePlus size={12} /> Raise action{actionCount ? ` (${actionCount})` : ""}
                        </button>
                    )}
                </div>
                {isNo && !readonly && actionCount === 0 && (
                    <p className="text-[11px] mt-1.5 inline-flex items-center gap-1" style={{ color: "#B33A2E" }}>
                        <AlertTriangle size={11} /> Non-compliance — consider raising an action.
                    </p>
                )}
                {(photos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid={`field-${field.id}-photos`}>
                        {photos.map((p) => (
                            <div key={p.id} className="relative w-16 h-16">
                                <PhotoImg testId={`field-${field.id}-photo-${p.id}`}
                                    src={`${API}/forms/instances/${instanceId}/photos/${p.id}/file`} alt={p.filename}
                                    className="w-16 h-16 object-cover rounded-lg border border-[#E5E0DA]" />
                                {!readonly && onDeletePhoto && (
                                    <button type="button" data-testid={`field-${field.id}-photo-delete-${p.id}`}
                                        onClick={() => { if (window.confirm("Remove this photo?")) onDeletePhoto(p.id); }}
                                        title="Remove photo"
                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]">
                                        <X size={11} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <PendingPhotoThumbs pending={pendingPhotos} testId={`field-${field.id}-pending-photos`} onRetry={onRetryPhoto} onDiscard={readonly ? null : onDiscardPhoto} />
                <input data-testid={`field-${field.id}-comment`} value={comment || ""} disabled={readonly}
                    onChange={(e) => onComment?.(e.target.value)} placeholder="Comment (optional)…"
                    className="mt-2 w-full px-3 py-1.5 text-[12.5px] border rounded-lg border-[#E5E0DA] outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                {helpEl}
            </div>
        );
    }
    if (ftype === "score") {
        const SCORE_COLORS = { "5": "#1B5E20", "4": "#2E7D32", "3": "#7CB342", "2": "#E08600", "1": "#C62828" };
        const lead = String(value || "").trim()[0];
        const isLow = lead === "1" || lead === "2";
        return (
            <div className="pb-1" data-testid={`field-wrap-${field.id}`}>
                {labelEl}
                <div className="flex flex-wrap gap-1.5 items-center" data-testid={`field-${field.id}`}>
                    {(field.options || []).map((opt) => {
                        const sel = value === opt;
                        const c = SCORE_COLORS[opt.trim()[0]] || "#6F6A64";
                        return (
                            <button key={opt} type="button" disabled={readonly}
                                data-testid={`field-${field.id}-${opt.trim()[0].toLowerCase()}`}
                                onClick={() => onChange(opt)}
                                className="px-3 py-1.5 text-[12.5px] font-semibold rounded-lg border transition-all"
                                style={{
                                    borderColor: sel ? c : "#E5E0DA",
                                    background: sel ? c : "#fff",
                                    color: sel ? "#fff" : "#1F1F1F",
                                }}>
                                {opt}
                            </button>
                        );
                    })}
                    {!readonly && onRaiseAction && (
                        <button type="button" data-testid={`field-${field.id}-raise-action`} onClick={() => onRaiseAction()}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${isLow ? "text-white border-[#C62828]" : "text-[#1F1F1F] bg-white border-[#E5E0DA] hover:bg-[#F6F4F0]"}`}
                            style={isLow ? { background: "#C62828" } : undefined}>
                            <MessageSquarePlus size={12} /> Raise action{actionCount ? ` (${actionCount})` : ""}
                        </button>
                    )}
                </div>
                {isLow && !readonly && actionCount === 0 && (
                    <p className="text-[11px] mt-1.5 inline-flex items-center gap-1" style={{ color: "#C62828" }}>
                        <AlertTriangle size={11} /> Low score — consider raising an action.
                    </p>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "dropdown") {
        const bmap = field.dropdown_config?.building_map || {};
        const hasRotation = Object.keys(bmap).length > 0;
        const lfm = field.dropdown_config?.linked_field_map;
        const lfmAutoVal = lfm ? (lfm.value_map || {})[allAnswers?.[lfm.field_id]] : null;
        // Only lock once a value has actually been resolved (backend pre-fill or a
        // prior pick) — a building_map can exist with its options still unmapped
        // (e.g. PL-ASPIRE-FC seeded on LIVE ahead of its buildings existing), and
        // locking an EMPTY select would leave every non-internal user stuck.
        // For linked_field_map, a saved value that no longer matches the live
        // auto value is a deliberate override (persisted across reloads) — show
        // it unlocked + "Manually set" rather than snapping back to auto.
        const lfmManual = !!lfm && !!value && value !== lfmAutoVal;
        const isLocked = (hasRotation && dropdownLocked && !!value) || (!!lfm && dropdownLocked && !!lfmAutoVal && !lfmManual);
        const unresolved = hasRotation && isInternal && !!buildingId
            && !Object.values(bmap).includes(buildingId)
            && (field.options || []).some((o) => !bmap[o]);
        return (
            <div>
                {labelEl}
                <div className="flex items-center gap-2">
                    <select data-testid={`field-${field.id}`}
                        value={value || ""} disabled={readonly || isLocked}
                        onChange={(e) => onChange(e.target.value)} className={inputCls + " flex-1"}>
                        <option value="">— Select —</option>
                        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {isLocked && (
                        <span title={lfm ? "Pre-selected based on the linked field's answer" : "Pre-selected based on building rotation for this site"} className="text-[#9A968F] shrink-0"><Lock size={13} /></span>
                    )}
                    {isLocked && isInternal && !readonly && (
                        <button type="button" data-testid={`field-${field.id}-unlock`} onClick={onUnlockDropdown}
                            className="text-[11px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline shrink-0">Unlock</button>
                    )}
                </div>
                {isLocked && (
                    <p className="text-[11px] text-[#9A968F] mt-1">
                        {lfm ? "Pre-selected based on the linked field's answer." : "Pre-selected based on building rotation for this site."}{isInternal ? " Click Unlock to change it." : ""}
                    </p>
                )}
                {lfmManual && (
                    <p className="text-[11px] mt-1" style={{ color: "#B8862B" }}>Manually set</p>
                )}
                {unresolved && (
                    <p data-testid={`field-${field.id}-unlinked-notice`} className="text-[11px] mt-1" style={{ color: "#B8862B" }}>
                        Link this option to a building in Form Builder.
                    </p>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "multi_select") {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (opt) => {
            if (readonly) return;
            onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
        };
        return (
            <div>
                {labelEl}
                <div className="flex flex-wrap gap-1.5" data-testid={`field-${field.id}`}>
                    {(field.options || []).map((opt) => {
                        const sel = arr.includes(opt);
                        return (
                            <button key={opt} type="button" disabled={readonly}
                                onClick={() => toggle(opt)}
                                className={`px-3 py-1 text-[12px] font-semibold rounded-full border ${sel
                                        ? "bg-[var(--brand-accent,_#C75A1B)] text-white border-[var(--brand-accent,_#C75A1B)]"
                                        : "text-[#1F1F1F] border-[#E5E0DA] bg-white hover:border-[#C0BCB4]"
                                    }`}>{opt}</button>
                        );
                    })}
                </div>
                {helpEl}
            </div>
        );
    }
    if (ftype === "checkbox") {
        return (
            <label className="flex items-center gap-2 text-sm text-[#1F1F1F]" data-testid={`field-${field.id}`}>
                <input type="checkbox" checked={!!value} disabled={readonly}
                    onChange={(e) => onChange(e.target.checked)} />
                {field.label}{field.required && <span className="text-red-600">*</span>}
            </label>
        );
    }
    if (ftype === "photo") {
        const coverSection = (template?.sections || []).find((s) => s.title?.trim().toLowerCase() === "front cover");
        const isCoverPhoto = (coverSection?.fields || []).find((f) => f.field_type === "photo")?.id === field.id;
        return (
            <div>
                {labelEl}
                {isCoverPhoto && <p data-testid="cover-photo-quality-hint" className="text-[11px] text-[#6F6A64] mb-2">
                    For a sharp PDF and Word cover, use the original, in-focus camera photo. Aim for at least 2100 × 1300 pixels in landscape.
                </p>}
                <div className={`flex items-center gap-3 flex-wrap rounded-lg transition-colors ${dragOver ? "bg-[#FBEFE5] ring-2 ring-[var(--brand-accent,_#C75A1B)] ring-offset-2" : ""}`}
                    data-testid={`field-${field.id}`}
                    onDragOver={(e) => { if (readonly) return; e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        if (readonly) return;
                        e.preventDefault(); setDragOver(false);
                        if (e.dataTransfer?.files?.length) onPhoto(e.dataTransfer.files);
                    }}>
                    {(photos || []).map((p) => (
                        <div key={p.id} data-testid={`field-${field.id}-photo-${p.id}`}
                            className="relative rounded-lg border border-[#E5E0DA] overflow-hidden bg-white w-28">
                            <PhotoImg testId={`field-${field.id}-photo-img-${p.id}`}
                                src={`${API}/forms/instances/${instanceId}/photos/${p.id}/file`} alt={p.caption || p.filename}
                                className="w-full h-20 object-cover bg-[#F6F4F0]" />
                            <div className="px-1.5 py-1 text-[10.5px] text-[#6F6A64] truncate flex items-center gap-1">
                                <Camera size={10} className="shrink-0" /> <span className="truncate">{p.filename}</span>
                            </div>
                            {!readonly && onDeletePhoto && (
                                <button type="button" data-testid={`field-${field.id}-delete-${p.id}`}
                                    onClick={() => onDeletePhoto(p.id)} title="Remove photo"
                                    className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]">
                                    <X size={11} />
                                </button>
                            )}
                        </div>
                    ))}
                    {(pendingPhotos || []).map((p) => (
                        <div key={p.tempId} data-testid={`field-${field.id}-queued-photo-${p.tempId}`}
                            className={`px-3 py-2 rounded-lg border border-dashed text-[12px] flex items-center gap-2 ${p.failed ? "border-[#B3261E] bg-[#FBE9E7] text-[#8A2C22]" : "border-[#C9A66B] bg-[#FBEFE3] text-[#9F4212]"}`}>
                            {p.failed ? <AlertTriangle size={12} /> : <CloudOff size={12} />}
                            <span className="truncate max-w-[140px]">{p.filename}</span>
                            <span className="text-[10px] font-semibold shrink-0">{p.retrying ? "retrying…" : p.failed ? `· failed (${p.error || "error"})` : "· queued"}</span>
                            {!readonly && p.failed && onRetryPhoto && (
                                <button type="button" data-testid={`field-${field.id}-photo-retry-${p.tempId}`}
                                    onClick={() => onRetryPhoto(p.tempId)} disabled={p.retrying}
                                    className="text-[10px] font-bold underline shrink-0">Retry</button>
                            )}
                            {!readonly && onDiscardPhoto && (
                                <button type="button" data-testid={`field-${field.id}-photo-discard-${p.tempId}`}
                                    onClick={() => { if (window.confirm("Discard this photo? It will not be uploaded.")) onDiscardPhoto(p.tempId); }}
                                    title="Discard this photo" className="ml-auto shrink-0 w-4 h-4 grid place-items-center rounded-full bg-black/10 hover:bg-[#B33A2E] hover:text-white">
                                    <X size={10} />
                                </button>
                            )}
                        </div>
                    ))}
                    {!readonly && (
                        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)] cursor-pointer text-[13px] font-semibold">
                            {uploading ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
                            {dragOver ? "Drop to upload" : (photos || []).length ? "Add another photo" : "Upload photo or drag & drop"}
                            <input type="file" accept="image/*" multiple className="hidden"
                                data-testid={`field-${field.id}-input`}
                                onChange={(e) => { onPhoto(e.target.files); e.target.value = ""; }} />
                        </label>
                    )}
                </div>
                {helpEl}
            </div>
        );
    }
    if (ftype === "external_name") {
        return (
            <div>
                {labelEl}
                <input data-testid={`field-${field.id}`}
                    value={value || ""} disabled={readonly}
                    placeholder={field.placeholder || "Full name"}
                    onChange={(e) => onChange(e.target.value)} className={inputCls} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "internal_name") {
        const names = staff.map((s) => s.name).filter(Boolean);
        const missing = value && !names.includes(value);
        return (
            <div>
                {labelEl}
                <select data-testid={`field-${field.id}`}
                    value={value || ""} disabled={readonly}
                    onChange={(e) => onChange(e.target.value)} className={inputCls}>
                    <option value="">Select PR staff…</option>
                    {missing && <option value={value}>{value}</option>}
                    {staff.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}{s.role ? ` · ${s.role}` : ""}</option>
                    ))}
                </select>
                {helpEl}
            </div>
        );
    }
    if (ftype === "signature") {
        // Inline signature for non-final sections — uses local placeholder
        return (
            <div className="text-[12px] text-[#9A968F] italic">(Signature captured below the form sections.)</div>
        );
    }
    if (ftype === "richtext") {
        const displayLabel = fieldLabel || field.label;
        return (
            <div data-testid={`richtext-field-${field.id}`}>
                {field.heading_editable && !readonly ? (
                    <div className="flex items-center gap-1.5 mb-2">
                        <Pencil size={12} className="text-[#9A968F] shrink-0" />
                        <input data-testid={`richtext-heading-${field.id}`} defaultValue={displayLabel}
                            onBlur={(e) => { if (e.target.value.trim() && e.target.value !== displayLabel) onUpdateFieldLabel(e.target.value.trim()); }}
                            className="flex-1 text-[13px] font-semibold text-[#1F1F1F] border-b border-dashed border-[#C0BCB4] focus:border-[var(--brand-accent,_#C75A1B)] outline-none bg-transparent py-0.5" />
                    </div>
                ) : (
                    <label className="block text-[13px] font-semibold text-[#1F1F1F] mb-2">{displayLabel}</label>
                )}
                <textarea rows={4} className={inputCls + " h-auto resize-y"}
                    placeholder="Type here — use **bold** or start a line with - for a bullet point"
                    value={value || ""} disabled={readonly} data-testid={`richtext-value-${field.id}`}
                    onChange={(e) => onChange(e.target.value)} />
                {helpEl}
            </div>
        );
    }
    if (ftype === "key_findings_auto") {
        const rows = computeKeyFindings(template || {}, allAnswers || {});
        return (
            <div data-testid={`key-findings-${field.id}`}>
                {labelEl}
                {rows.length === 0 ? (
                    <p className="text-[12px] italic text-[#9A968F]">No findings recorded yet — add findings in the Findings section.</p>
                ) : (
                    <div className="overflow-x-auto border border-[#E5E0DA] rounded-lg">
                        <table className="w-full text-[12.5px]">
                            <thead><tr className="bg-[#F6F4F0]">
                                {["#", "Finding", "Requirement", "Action", "Status"].map((h) => (
                                    <th key={h} className="text-left px-2.5 py-2 font-semibold text-[#6F6A64] whitespace-nowrap">{h}</th>
                                ))}
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.num} data-testid={`key-finding-row-${r.num}`} className="border-t border-[#F0EDE8]">
                                        <td className="px-2.5 py-2 font-bold">{r.num}</td>
                                        <td className="px-2.5 py-2">{r.title || "—"}</td>
                                        <td className="px-2.5 py-2">{r.requirement || "—"}</td>
                                        <td className="px-2.5 py-2">{r.action || "—"}</td>
                                        <td className="px-2.5 py-2">{r.status || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "subsection_list") {
        const rows = Array.isArray(value) ? value : [];
        const secNum = (template?.sections || []).filter((s) => (s.title || "").trim().toLowerCase() !== "front cover")
            .findIndex((s) => (s.fields || []).some((f) => f.id === field.id)) + 1 || 2;
        const update = (idx, key, v) => onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r)));
        const addRow = () => onChange([...rows, { id: crypto.randomUUID(), heading: "", description: "", locked: false }]);
        const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
        const move = (idx, dir) => {
            const j = idx + dir;
            if (j < 0 || j >= rows.length) return;
            const next = [...rows];
            [next[idx], next[j]] = [next[j], next[idx]];
            onChange(next);
        };
        return (
            <div data-testid={`subsection-list-${field.id}`}>
                {labelEl}
                <div className="space-y-4">
                    {rows.map((r, idx) => (
                        <div key={r.id} data-testid={`subsection-row-${idx}`} className="rounded-xl border border-[#E5E0DA] p-3 bg-white">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[12.5px] font-bold text-[#9A968F] shrink-0">{secNum}.{idx + 1}</span>
                                <input data-testid={`subsection-heading-${idx}`} className={inputCls} placeholder="Subsection heading"
                                    value={r.heading || ""} disabled={readonly} onChange={(e) => update(idx, "heading", e.target.value)} />
                                {!readonly && (
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <button type="button" data-testid={`subsection-up-${idx}`} disabled={idx === 0} onClick={() => move(idx, -1)}
                                            className="w-7 h-7 grid place-items-center rounded hover:bg-[#F6F4F0] disabled:opacity-30"><ChevronUp size={14} /></button>
                                        <button type="button" data-testid={`subsection-down-${idx}`} disabled={idx === rows.length - 1} onClick={() => move(idx, 1)}
                                            className="w-7 h-7 grid place-items-center rounded hover:bg-[#F6F4F0] disabled:opacity-30"><ChevronDown size={14} /></button>
                                        {!r.locked && (
                                            <button type="button" data-testid={`subsection-remove-${idx}`} onClick={() => removeRow(idx)}
                                                className="w-7 h-7 grid place-items-center rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <textarea rows={3} className={inputCls + " h-auto resize-none"} placeholder="Description"
                                value={r.description || ""} disabled={readonly} data-testid={`subsection-description-${idx}`}
                                onChange={(e) => update(idx, "description", e.target.value)} />
                            <RowPhotoStrip rowId={r.id} photos={photos} pendingPhotos={pendingPhotos} onPhoto={onPhoto}
                                onDeletePhoto={onDeletePhoto} onUpdatePhoto={onUpdatePhoto} onRetryPhoto={onRetryPhoto}
                                onDiscardPhoto={onDiscardPhoto} uploading={uploading} readonly={readonly} instanceId={instanceId} />
                        </div>
                    ))}
                    {rows.length === 0 && <p className="text-[12px] italic text-[#9A968F]">No subsections yet.</p>}
                </div>
                {!readonly && (
                    <button type="button" data-testid={`subsection-add-${field.id}`} onClick={addRow}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}><Plus size={13} /> Add subsection</button>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "finding_repeater") {
        const rows = Array.isArray(value) ? value : [];
        const STATUS_OPTS = ["Open", "In progress", "Closed – satisfactory", "Closed"];
        const update = (idx, key, v) => onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r)));
        const addRow = () => onChange([...rows, { id: crypto.randomUUID(), title: "", observation: "", requirement: "", action: "", owner: "", status: "Open" }]);
        const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
        return (
            <div data-testid={`finding-repeater-${field.id}`}>
                {labelEl}
                <div className="space-y-4">
                    {rows.map((r, idx) => {
                        const actionKey = `${field.id}:${r.id}`;
                        const linkedIds = fieldActions[actionKey] || [];
                        const linkedActions = instanceActions.filter((a) => linkedIds.includes(a.id));
                        return (
                            <div key={r.id} data-testid={`finding-row-${idx}`} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
                                <div className="px-3 py-2 flex items-center justify-between" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                    <span className="text-[12.5px] font-bold text-white">F{idx + 1}</span>
                                    {!readonly && (
                                        <button type="button" data-testid={`finding-remove-${idx}`} onClick={() => removeRow(idx)}
                                            className="text-[11px] font-semibold text-white/90 hover:underline">Remove</button>
                                    )}
                                </div>
                                <div className="p-3 space-y-2">
                                    <input data-testid={`finding-title-${idx}`} className={inputCls} placeholder="Finding title"
                                        value={r.title || ""} disabled={readonly} onChange={(e) => update(idx, "title", e.target.value)} />
                                    <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Observation"
                                        value={r.observation || ""} disabled={readonly} data-testid={`finding-observation-${idx}`}
                                        onChange={(e) => update(idx, "observation", e.target.value)} />
                                    <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Requirement"
                                        value={r.requirement || ""} disabled={readonly} data-testid={`finding-requirement-${idx}`}
                                        onChange={(e) => update(idx, "requirement", e.target.value)} />
                                    <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Action required"
                                        value={r.action || ""} disabled={readonly} data-testid={`finding-action-${idx}`}
                                        onChange={(e) => update(idx, "action", e.target.value)} />
                                    <div className="grid grid-cols-2 gap-2">
                                        <input data-testid={`finding-owner-${idx}`} className={inputCls} placeholder="Owner"
                                            value={r.owner || ""} disabled={readonly} onChange={(e) => update(idx, "owner", e.target.value)} />
                                        <select className={inputCls} value={r.status || "Open"} disabled={readonly}
                                            data-testid={`finding-status-${idx}`} onChange={(e) => update(idx, "status", e.target.value)}>
                                            {STATUS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <RowPhotoStrip rowId={r.id} photos={photos} pendingPhotos={pendingPhotos} onPhoto={onPhoto}
                                        onDeletePhoto={onDeletePhoto} onUpdatePhoto={onUpdatePhoto} onRetryPhoto={onRetryPhoto}
                                        onDiscardPhoto={onDiscardPhoto} uploading={uploading} readonly={readonly} instanceId={instanceId} />
                                    {isInternal && (
                                        <FindingActionBar linkedActions={linkedActions} readonly={readonly} clientId={clientId}
                                            onCreate={() => onRaiseAction(r.id, r.title || `Finding F${idx + 1}`, { observation: r.observation, title: r.title || `Finding F${idx + 1}` })}
                                            onLink={(actionId) => onLinkAction(field.id, actionId, r.id)} />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {rows.length === 0 && <p className="text-[12px] italic text-[#9A968F]">No findings added yet.</p>}
                </div>
                {!readonly && (
                    <button type="button" data-testid={`finding-add-${field.id}`} onClick={addRow}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}><Plus size={13} /> Add finding</button>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "attendee_table") {
        const rows = Array.isArray(value) ? value : [];
        const update = (idx, key, v) => {
            const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
            onChange(next);
        };
        const addRow = () => onChange([...rows, { name: "", position: "", signature: "", date: new Date().toISOString().slice(0, 10) }]);
        const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
        return (
            <div data-testid={`attendee-table-${field.id}`}>
                {labelEl}
                <div className="space-y-3">
                    {rows.map((r, idx) => (
                        <div key={idx} data-testid={`attendee-row-${idx}`} className="rounded-lg border border-[#E5E0DA] p-3 bg-white">
                            <div className="grid sm:grid-cols-3 gap-2 mb-2">
                                <input className={inputCls} placeholder="Name" value={r.name || ""} disabled={readonly}
                                    data-testid={`attendee-name-${idx}`} onChange={(e) => update(idx, "name", e.target.value)} />
                                <input className={inputCls} placeholder="Position / Company" value={r.position || ""} disabled={readonly}
                                    data-testid={`attendee-position-${idx}`} onChange={(e) => update(idx, "position", e.target.value)} />
                                <input type="date" className={inputCls} value={r.date || ""} disabled={readonly}
                                    data-testid={`attendee-date-${idx}`} onChange={(e) => update(idx, "date", e.target.value)} />
                            </div>
                            <SignaturePad value={r.signature || ""} onChange={(v) => update(idx, "signature", v)}
                                disabled={readonly} label="Attendee signature" />
                            {!readonly && (
                                <button type="button" data-testid={`attendee-remove-${idx}`} onClick={() => removeRow(idx)}
                                    className="mt-2 text-[11px] font-semibold text-red-600 hover:underline">Remove attendee</button>
                            )}
                        </div>
                    ))}
                    {rows.length === 0 && <p className="text-[12px] italic text-[#9A968F]">No attendees added yet.</p>}
                </div>
                {!readonly && (
                    <button type="button" data-testid={`attendee-add-${field.id}`} onClick={addRow}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>+ Add attendee</button>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "observation_table") {
        const rows = Array.isArray(value) ? value : [];
        const STATUS_OPTS = ["In Progress", "Satisfactory", "Complete", "Action Required", "Planned"];
        const update = (idx, key, v) => {
            const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
            onChange(next);
        };
        const addRow = () => onChange([...rows, { observation: "", action: "", status: "In Progress" }]);
        const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
        return (
            <div data-testid={`observation-table-${field.id}`}>
                {labelEl}
                <div className="space-y-3">
                    {rows.map((r, idx) => (
                        <div key={idx} data-testid={`observation-row-${idx}`} className="rounded-lg border border-[#E5E0DA] p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] font-bold text-[#9A968F]">Item {idx + 1}</span>
                                {!readonly && (
                                    <button type="button" data-testid={`observation-remove-${idx}`} onClick={() => removeRow(idx)}
                                        className="text-[11px] font-semibold text-red-600 hover:underline">Remove</button>
                                )}
                            </div>
                            <div className="grid gap-2 mb-2">
                                <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Observation / finding"
                                    value={r.observation || ""} disabled={readonly} data-testid={`observation-finding-${idx}`}
                                    onChange={(e) => update(idx, "observation", e.target.value)} />
                                <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Action required"
                                    value={r.action || ""} disabled={readonly} data-testid={`observation-action-${idx}`}
                                    onChange={(e) => update(idx, "action", e.target.value)} />
                                <select className={inputCls + " w-48"} value={r.status || "In Progress"} disabled={readonly}
                                    data-testid={`observation-status-${idx}`}
                                    onChange={(e) => update(idx, "status", e.target.value)}>
                                    {STATUS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        </div>
                    ))}
                    {rows.length === 0 && <p className="text-[12px] italic text-[#9A968F]">No observations added yet.</p>}
                </div>
                {!readonly && (
                    <button type="button" data-testid={`observation-add-${field.id}`} onClick={addRow}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>+ Add observation</button>
                )}
                {helpEl}
            </div>
        );
    }
    if (ftype === "area_progress_table") {
        const rows = Array.isArray(value) && value.length ? value : [{ area_name: "", progress_notes: "", comments: "" }];
        const update = (idx, key, v) => {
            const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
            onChange(next);
        };
        const addRow = () => onChange([...rows, { area_name: "", progress_notes: "", comments: "" }]);
        const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
        return (
            <div data-testid={`area-progress-table-${field.id}`}>
                {labelEl}
                <div className="space-y-3">
                    {rows.map((r, idx) => (
                        <div key={idx} data-testid={`area-progress-row-${idx}`} className="rounded-lg border border-[#E5E0DA] p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] font-bold text-[#9A968F]">Area {idx + 1}</span>
                                {!readonly && rows.length > 1 && (
                                    <button type="button" data-testid={`area-progress-remove-${idx}`} onClick={() => removeRow(idx)}
                                        className="text-[11px] font-semibold text-red-600 hover:underline">Remove</button>
                                )}
                            </div>
                            <label className="block text-[12.5px] font-semibold text-[#1F1F1F] mb-1">Area / Level name</label>
                            <input className={inputCls} placeholder="e.g. Basement Level, Ground Floor, Roof Terrace…"
                                value={r.area_name || ""} disabled={readonly} data-testid={`area-progress-name-${idx}`}
                                onChange={(e) => update(idx, "area_name", e.target.value)} />
                            <p className="text-[11px] text-[#9A968F] mt-1 mb-2">Editable — name the level/area this project's report covers.</p>
                            <label className="block text-[12.5px] font-semibold text-[#1F1F1F] mb-1">Progress notes</label>
                            <textarea rows={3} className={inputCls + " h-auto resize-none mb-2"}
                                value={r.progress_notes || ""} disabled={readonly} data-testid={`area-progress-notes-${idx}`}
                                onChange={(e) => update(idx, "progress_notes", e.target.value)} />
                            <label className="block text-[12.5px] font-semibold text-[#1F1F1F] mb-1">Comments</label>
                            <textarea rows={2} className={inputCls + " h-auto resize-none"} placeholder="Any additional comments for this area…"
                                value={r.comments || ""} disabled={readonly} data-testid={`area-progress-comments-${idx}`}
                                onChange={(e) => update(idx, "comments", e.target.value)} />
                        </div>
                    ))}
                </div>
                {!readonly && (
                    <button type="button" data-testid={`area-progress-add-${field.id}`} onClick={addRow}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>+ Add area</button>
                )}
                {helpEl}
            </div>
        );
    }
    return <div className="text-[12px] text-[#9A968F]">Unsupported field type: {ftype}</div>;
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

const CLASSIFICATIONS = ["Non-conformance", "Opportunity for Improvement", "Major", "Observation"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

function ActionsList({ actions }) {
    if (!actions || actions.length === 0) {
        return <p className="text-[12.5px] italic text-[#9A968F]" data-testid="actions-list-empty">No actions raised yet. Use "Raise action" on any question to add one.</p>;
    }
    const tone = { Open: "amber", "In progress": "blue", Complete: "green", Blocked: "red" };
    return (
        <div className="space-y-2" data-testid="actions-list">
            {actions.map((a, i) => (
                <div key={a.id} data-testid={`action-row-${a.id}`} className="rounded-lg border border-[#E5E0DA] p-3">
                    <div className="flex items-start gap-2">
                        <span className="w-6 h-6 grid place-items-center rounded text-[11px] font-bold text-white shrink-0" style={{ background: "var(--brand-accent,#C75A1B)" }}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1F1F1F]">{a.title}</div>
                            {a.description && <div className="text-[12px] text-[#4A4A4A] mt-0.5">{a.description}</div>}
                            <div className="flex flex-wrap gap-2 mt-1.5 text-[11px] text-[#6F6A64]">
                                {a.kind && <span className="px-2 py-0.5 rounded-full bg-[#F0EDE8]">{a.kind}</span>}
                                <span>Assigned: {a.assignee_name || "TBC"}</span>
                                <span>Due: {a.due_date || "TBC"}</span>
                            </div>
                        </div>
                        <Pill tone={tone[a.status] || "neutral"}>{a.status}</Pill>
                    </div>
                </div>
            ))}
        </div>
    );
}

function FindingActionBar({ linkedActions, readonly, onCreate, onLink, clientId }) {
    const [linking, setLinking] = useState(false);
    const [options, setOptions] = useState([]);
    const [picked, setPicked] = useState("");
    const [loading, setLoading] = useState(false);
    const startLink = async () => {
        setLinking(true);
        setLoading(true);
        try {
            const { data } = await api.get("/actions", { params: { status: "Open", ...(clientId ? { client_id: clientId } : {}) } });
            const linkedIds = new Set((linkedActions || []).map((a) => a.id));
            setOptions((data || []).filter((a) => !linkedIds.has(a.id)));
        } catch { setOptions([]); }
        setLoading(false);
    };
    return (
        <div className="pt-2 border-t border-[#F0EDE8] mt-2">
            {linkedActions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {linkedActions.map((a) => (
                        <span key={a.id} data-testid={`finding-linked-action-${a.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: "#EAF4EC", color: "#1E7C3A" }}>
                            <Link2 size={10} /> {a.ref || a.title}
                        </span>
                    ))}
                </div>
            )}
            {!readonly && (
                <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" data-testid="finding-create-action" onClick={onCreate}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11.5px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64] hover:bg-[#F6F4F0]">
                        <ClipboardList size={12} /> Create action
                    </button>
                    {!linking ? (
                        <button type="button" data-testid="finding-link-action-start" onClick={startLink}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11.5px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64] hover:bg-[#F6F4F0]">
                            <Link2 size={12} /> Link existing action
                        </button>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            <select data-testid="finding-link-action-select" className="h-8 px-2 text-[11.5px] border border-[#E5E0DA] rounded-lg"
                                value={picked} onChange={(e) => setPicked(e.target.value)}>
                                <option value="">{loading ? "Loading…" : "— Select action —"}</option>
                                {options.map((a) => <option key={a.id} value={a.id}>{a.ref ? `${a.ref} — ` : ""}{a.title}</option>)}
                            </select>
                            <button type="button" data-testid="finding-link-action-confirm" disabled={!picked}
                                onClick={() => { onLink(picked); setLinking(false); setPicked(""); }}
                                className="px-2.5 py-1.5 text-[11.5px] font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                Link
                            </button>
                            <button type="button" data-testid="finding-link-action-cancel" onClick={() => setLinking(false)}
                                className="px-2 py-1.5 text-[11.5px] text-[#9A968F]">Cancel</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RaiseActionDialog({ label, initialObservation, onClose, onSubmit }) {
    const [f, setF] = useState({ observation: initialObservation || "", assignee_name: "", due_date: "", classification: "Non-conformance", priority: "Medium" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    async function submit() {
        if (!f.observation.trim()) { toast.error("Describe the observation"); return; }
        setBusy(true);
        await onSubmit({ ...f, observation: f.observation.trim() });
        setBusy(false);
    }
    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="raise-action-dialog" className="bg-white rounded-2xl shadow-brand-lg w-full max-w-[520px] border border-[#E5E0DA]">
                <div className="px-5 pt-4 pb-3 border-b border-[#F0EDE8] flex items-center justify-between">
                    <div>
                        <h3 className="font-display text-[16px] font-bold text-[#1F1F1F] flex items-center gap-2"><ClipboardList size={16} /> Raise action</h3>
                        <p className="text-[11px] text-[#9A968F] mt-0.5 truncate max-w-[400px]">{label}</p>
                    </div>
                    <button data-testid="raise-action-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="px-5 py-4 space-y-3">
                    <label className="block">
                        <span className="text-[11px] font-semibold mb-1 block text-[#6F6A64]">Observation description *</span>
                        <textarea data-testid="raise-action-observation" rows={3} value={f.observation} onChange={(e) => set("observation", e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none border-[#E5E0DA] focus:border-[var(--brand-accent,_#C75A1B)] resize-none"
                            placeholder="Describe the non-compliance / observation and required remedial action…" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[11px] font-semibold mb-1 block text-[#6F6A64]">Classification</span>
                            <select data-testid="raise-action-classification" value={f.classification} onChange={(e) => set("classification", e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none border-[#E5E0DA]">
                                {CLASSIFICATIONS.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-semibold mb-1 block text-[#6F6A64]">Priority</span>
                            <select data-testid="raise-action-priority" value={f.priority} onChange={(e) => set("priority", e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none border-[#E5E0DA]">
                                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-semibold mb-1 block text-[#6F6A64]">Assigned to</span>
                            <input data-testid="raise-action-assignee" value={f.assignee_name} onChange={(e) => set("assignee_name", e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none border-[#E5E0DA]" placeholder="Name" />
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-semibold mb-1 block text-[#6F6A64]">Due date</span>
                            <input data-testid="raise-action-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none border-[#E5E0DA]" />
                        </label>
                    </div>
                    <p className="text-[11px] text-[#9A968F]">Photos added to this question are attached to the report alongside the action.</p>
                </div>
                <div className="px-5 py-3 border-t border-[#F0EDE8] flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold border bg-white border-[#E5E0DA]">Cancel</button>
                    <button data-testid="raise-action-submit" onClick={submit} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle size={13} />} Raise action
                    </button>
                </div>
            </div>
        </div>
    );
}

function SectionPhotos({ sectionId, instanceId, photos, pendingPhotos = [], uploading, readonly, onUpload, onUpdate, onReorder, onDelete, onRetryPhoto, onDiscardPhoto }) {
    const WIDTHS = [["small", "S"], ["medium", "M"], ["full", "Full"]];
    const [dragOver, setDragOver] = useState(false);
    const [draggedId, setDraggedId] = useState(null);
    const [overId, setOverId] = useState(null);
    const ordered = [...photos].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    function handleCardDragStart(e, photoId) {
        if (readonly) return;
        setDraggedId(photoId);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", photoId);
    }
    function handleCardDragOver(e, photoId) {
        if (readonly || !draggedId) return;
        e.preventDefault(); e.stopPropagation();
        if (overId !== photoId) setOverId(photoId);
    }
    function handleCardDrop(e, targetId) {
        if (readonly || !draggedId) return;
        e.preventDefault(); e.stopPropagation();
        setOverId(null);
        const fromIdx = ordered.findIndex((p) => p.id === draggedId);
        const toIdx = ordered.findIndex((p) => p.id === targetId);
        setDraggedId(null);
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
        const next = [...ordered];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        const changes = next
            .map((p, i) => ({ id: p.id, sort_order: i, was: p.sort_order || 0 }))
            .filter((c) => c.sort_order !== c.was)
            .map(({ id: pid, sort_order }) => ({ id: pid, sort_order }));
        if (changes.length) onReorder(changes);
    }
    function handleCardDragEnd() { setDraggedId(null); setOverId(null); }

    return (
        <div className={`pt-4 border-t border-[#F0EDE8] rounded-lg transition-colors ${dragOver ? "bg-[#FBEFE5] ring-2 ring-[var(--brand-accent,_#C75A1B)] ring-offset-2" : ""}`}
            data-testid={`section-photos-${sectionId}`}
            onDragOver={(e) => { if (readonly || draggedId) return; e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                if (readonly || draggedId) return;
                e.preventDefault(); setDragOver(false);
                if (e.dataTransfer?.files?.length) onUpload(e.dataTransfer.files);
            }}>
            <div className="flex items-center justify-between mb-2">
                <div>
                    <p className="text-[12px] font-bold uppercase tracking-wider text-[#6F6A64]">Photos for this section</p>
                    {!readonly && (
                        <p className="text-[11px] text-[#9A968F] mt-0.5">
                            {dragOver ? "Drop to upload" : photos.length > 1 ? "Drag & drop images to upload, or drag a photo to reorder it" : "Drag & drop images here, or use Add image"}
                        </p>
                    )}
                </div>
                {!readonly && (
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)] cursor-pointer text-[12px] font-semibold">
                        {uploading ? <Loader2 className="animate-spin" size={13} /> : <ImagePlus size={13} />}
                        Add image
                        <input type="file" accept="image/*" multiple className="hidden"
                            data-testid={`section-photo-input-${sectionId}`}
                            onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
                    </label>
                )}
            </div>
            {photos.length === 0 && pendingPhotos.length === 0 ? (
                <p className="text-[11.5px] italic text-[#9A968F]">No images added to this section yet.</p>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {pendingPhotos.map((p) => (
                        <div key={p.tempId} className="relative">
                            <button type="button" onClick={() => p.failed && onRetryPhoto && onRetryPhoto(p.tempId)}
                                disabled={!p.failed || p.retrying}
                                data-testid={p.failed ? `section-pending-photo-failed-${p.tempId}` : `section-pending-photo-${p.tempId}`}
                                className={`relative w-full rounded-lg overflow-hidden bg-white text-left border-2 ${p.failed ? "border-solid border-[#B3261E] cursor-pointer" : "border-dashed border-[#C9A66B] cursor-default"}`}
                                title={p.failed ? `Failed to upload: ${p.error || "server error"} — tap to retry` : "Saved on this device — will upload once you're back online"}>
                                <img src={p.previewUrl} alt={p.filename} className="w-full h-32 object-cover opacity-60" />
                                <div className={`px-2 py-1 text-[10px] font-semibold flex items-center gap-1 ${p.failed ? "text-[#B3261E]" : "text-[#9F4212]"}`}>
                                    {p.retrying ? "Retrying…" : p.failed
                                        ? <><AlertTriangle size={10} /> Failed — tap to retry</>
                                        : <><CloudOff size={10} /> Queued — awaiting sync</>}
                                </div>
                            </button>
                            {!readonly && onDiscardPhoto && (
                                <button type="button" data-testid={`section-pending-photo-discard-${p.tempId}`}
                                    onClick={() => { if (window.confirm("Discard this photo? It will not be uploaded.")) onDiscardPhoto(p.tempId); }}
                                    title="Discard this photo"
                                    className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]">
                                    <X size={11} />
                                </button>
                            )}
                        </div>
                    ))}
                    {ordered.map((p) => (
                        <div key={p.id} data-testid={`section-photo-${p.id}`}
                            draggable={!readonly}
                            onDragStart={(e) => handleCardDragStart(e, p.id)}
                            onDragOver={(e) => handleCardDragOver(e, p.id)}
                            onDrop={(e) => handleCardDrop(e, p.id)}
                            onDragEnd={handleCardDragEnd}
                            className={`relative rounded-lg border overflow-hidden bg-white transition-all ${!readonly ? "cursor-move" : ""} ${draggedId === p.id ? "opacity-40" : ""} ${overId === p.id && draggedId && draggedId !== p.id ? "ring-2 ring-[var(--brand-accent,_#C75A1B)]" : "border-[#E5E0DA]"}`}>
                            {!readonly && (
                                <div className="absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded grid place-items-center bg-black/40 text-white" title="Drag to reorder">
                                    <GripVertical size={12} />
                                </div>
                            )}
                            <PhotoImg testId={`section-photo-img-${p.id}`}
                                src={`${API}/forms/instances/${instanceId}/photos/${p.id}/file`} alt={p.caption || p.filename}
                                className="w-full h-32 object-cover bg-[#F6F4F0]" draggable={false} />
                            <div className="p-2 space-y-1.5">
                                <input data-testid={`section-photo-caption-${p.id}`} defaultValue={p.caption || ""} disabled={readonly}
                                    onBlur={(e) => { if (e.target.value !== (p.caption || "")) onUpdate(p.id, { caption: e.target.value }); }}
                                    placeholder="Caption…" className="w-full px-2 py-1 text-[11.5px] border rounded border-[#E5E0DA] outline-none" />
                                {!readonly && (
                                    <div className="flex items-center justify-between gap-1">
                                        <div className="flex items-center gap-0.5">
                                            <button data-testid={`section-photo-anchor-${p.id}`} onClick={() => onUpdate(p.id, { anchor: p.anchor === "top" ? "bottom" : "top" })}
                                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-[#E5E0DA] hover:bg-[#F6F4F0]" title="Toggle placement">
                                                {p.anchor === "top" ? "Top" : "Bottom"}
                                            </button>
                                            {WIDTHS.map(([w, lbl]) => (
                                                <button key={w} onClick={() => onUpdate(p.id, { width: w })}
                                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${p.width === w ? "bg-[var(--brand-accent,_#C75A1B)] text-white border-[var(--brand-accent,_#C75A1B)]" : "border-[#E5E0DA] hover:bg-[#F6F4F0]"}`}>{lbl}</button>
                                            ))}
                                        </div>
                                        <button data-testid={`section-photo-delete-${p.id}`} onClick={() => onDelete(p.id)}
                                            className="w-6 h-6 grid place-items-center rounded hover:bg-[#FBE8E5]" title="Remove">
                                            <Trash2 size={13} className="text-[#B33A2E]" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
